import { Modalidade } from './src/lib/modalidade';

export type UserRole = 'adm' | 'gerente' | 'vendedor' | 'analista' | 'parceiro' | 'gerente_parcerias' | 'vendedor_parceiro' | 'motoboy' | 'empresa_parceira_admin' | 'empresa_parceira_operador' | 'consumidor';

export interface EmpresaParceira {
  id: string;
  nome: string;
  cnpj?: string | null;
  status: 'ativo' | 'inativo' | 'pendente';
  percentual_vigor: number;
  percentual_parceira: number;
  banco_conta: 'inter' | 'sicoob' | 'manual' | 'asaas';
  modulos_habilitados: string[];
  created_at: string;
  created_by?: string | null;
  /**
   * Identidade de quem EMITE a cobrança — a titular da conta bancária/Asaas em que ela
   * nasce — usada no bloco do emissor do boleto. Separada de `nome` (rótulo curto da UI)
   * e de `cnpj` (que pode ser outra empresa do mesmo grupo). Vazio = cai no titular da
   * UC, o comportamento anterior. Ver gerar-boleto-gran/desenhaPaginaGran.ts.
   */
  boleto_emissor_nome?: string | null;
  boleto_emissor_cnpj?: string | null;
  boleto_emissor_email?: string | null;
  boleto_emissor_telefone?: string | null;
}

export interface EmpresaParceiraBanco {
  id: string;
  empresa_parceira_id: string;
  banco: 'inter' | 'sicoob' | 'manual' | 'asaas';
  client_id?: string | null;
  client_secret_enc?: string | null;
  conta_corrente?: string | null;
  agencia?: string | null;
  certificado_cert_path?: string | null;
  certificado_key_path?: string | null;
  split_conta_vigor?: string | null;
  webhook_token_enc?: string | null;
  ambiente: 'sandbox' | 'producao';
  created_at: string;
  updated_at: string;
}

export interface Profile {
  id: string;
  full_name: string;
  role: UserRole;
  avatar_url: string;
  job_title?: string;
  status: 'active' | 'inactive';
  email?: string;
  whatsapp?: string;
  created_at: string;
  parceiro_id?: string | null;
  empresa_parceira_id?: string | null;
  departamento?: string; // legacy – single value
  departamentos?: string[]; // new – multi-department support
  permissions?: string[]; // Permissions array
  email_pessoal?: string | null; // e-mail pessoal do colaborador (opcional)
  email_corporativo?: string | null; // espelho do e-mail de login (@vigorenergy.com.br); usado nos signatários do termo
  cpf?: string | null; // exigido para atuar como testemunha do termo de adesão
  // Código de indicação (parceiro/vendedor_parceiro) — usado no link público de autocadastro de vendedor_parceiro
  codigo_indicacao?: string | null;
  // Vendedor externo que captou este parceiro (Nova Política Comercial — bonificação de rede + SLA)
  captado_por_id?: string | null;
  // CPF ou CNPJ do titular (role 'consumidor' — portal do consumidor); usado para vincular o consumidor às suas UCs/faturas
  cpf_cnpj?: string | null;
  // Cidade onde a pessoa trabalha — separa as equipes no BI de eficiência (Painel Executivo › Equipes). null = fora do BI.
  unidade?: 'Goiânia' | 'Morrinhos' | null;
  // Tela da Visão Geral quando a regra automática não serve (src/lib/visaoGeral/perfis.ts). null = automática.
  tela_inicial?: string | null;
  // Função no Comercial (Closer, BDR ou Hunter) — é ela que decide rodízio de leads e plano,
  // não o `job_title`. null = fora do Comercial. Ver src/lib/departamentos.ts.
  funcao_comercial?: 'closer' | 'bdr' | 'hunter' | null;
}

export interface Consumidor {
  id: string;
  cpf_cnpj: string;
  senha_provisoria_pendente: boolean;
  origem: 'manual' | 'carga_inicial' | 'automatico';
  criado_em: string;
  /** Último acesso ao portal. null = nunca entrou. */
  ultimo_acesso_em: string | null;
}

/** Aviso/comunicado do Portal do Consumidor (ver src/lib/resolverAvisosPendentes.ts). */
export interface AvisoConsumidor {
  id: string;
  titulo: string;
  mensagem: string;
  categoria: string;
  bloqueante: boolean;
  url_anexo?: string | null;
  data_inicio?: string | null;
  data_fim?: string | null;
  status: 'rascunho' | 'publicado' | 'arquivado';
  versao_anterior_id?: string | null;
  criado_por: string;
  criado_em: string;
  /** Quem vê no portal: 'todos', 'vigor' ou o slug da marca da parceira ('gran') — #6117. */
  publico?: string;
}

/** Confirmação de leitura/aceite de um consumidor para um AvisoConsumidor. */
export interface AvisoConsumidorConfirmacao {
  id: string;
  aviso_id: string;
  profile_id: string;
  visualizado_em: string | null;
  confirmado_em: string | null;
}

/** Aviso obrigatório pra equipe interna (ver src/lib/avisoAlvejaPerfil.ts). */
export interface AvisoSistema {
  id: string;
  titulo: string;
  mensagem: string;
  modelo: 'risco' | 'novidade';
  bloqueante: boolean;
  url_anexo?: string | null;
  data_inicio?: string | null;
  data_fim?: string | null;
  status: 'rascunho' | 'publicado' | 'arquivado';
  versao_anterior_id?: string | null;
  departamento_alvo?: string | null;
  unidade_geradora_site_id?: string | null;
  criado_por: string | null;
  criado_em: string;
}

/** Confirmação de leitura/aceite de um membro da equipe para um AvisoSistema. */
export interface AvisoSistemaConfirmacao {
  id: string;
  aviso_id: string;
  profile_id: string;
  visualizado_em: string | null;
  confirmado_em: string | null;
}

/**
 * Um item do feed persistente de Novidades TI — uma solicitação concluída na
 * Central de Solicitações (não um resumo agregado). Gerado automaticamente
 * (ver gerar_itens_novidades_ti no banco), sempre como rascunho até um adm
 * aprovar o lote do dia (data_referencia) pelo painel.
 */
export interface NovidadeTiItem {
  id: string;
  tarefa_id: number;
  titulo: string;
  titulo_original?: string | null;
  descricao?: string | null;
  categoria: 'novidade' | 'correcao';
  tag?: string | null;
  concluido_por: string | null;
  concluido_em: string;
  status: 'rascunho' | 'publicado';
  data_referencia: string;
  criado_em: string;
  incluir_publicacao: boolean;
  origem: 'tempo_real' | 'lote_noturno';
  departamentos_alvo?: string[] | null;
  publicado_em?: string | null;
}

/** Até quando (não o quê) cada pessoa já viu o feed de Novidades TI. */
export interface NovidadeTiVisualizacao {
  profile_id: string;
  ultima_visualizacao_em: string;
}

/**
 * Procedencia de uma resposta da Vick: qual consulta do catalogo a sustenta.
 *
 * `documentId` e `similarity` sobrevivem so para as 116 mensagens gravadas
 * antes de 30/08/2026, quando a Vick respondia por busca em documentos. Nas
 * respostas novas eles vem fixos (-1 e 1) e quem manda e `consulta`.
 */
export interface VickSource {
  documentId: number;
  titulo: string;
  similarity: number;
  /** Nome da consulta do catalogo, ex.: 'previsao_recebimento'. */
  consulta?: string;
  /** Parametros usados — e o que permite refazer a conta na mao. */
  parametros?: Record<string, unknown>;
}

/** Modulos que o vigia da Vick monitora. */
export type IaAlertaModulo = 'financeiro' | 'rateio' | 'comercial' | 'geradoras';
export type IaAlertaSeveridade = 'alta' | 'media' | 'baixa';
export type IaAlertaEstado = 'aberto' | 'resolvido' | 'ignorado';

/**
 * Um achado do vigia da Vick (tabela `ia_alertas`).
 *
 * Quem detecta e conta deterministica — as mesmas funcoes das telas. A IA le
 * `dados` para escrever o recado; `dados` e tambem o que permite conferir a
 * conta sem confiar no texto.
 */
export interface IaAlerta {
  id: number;
  tipo: string;
  modulo: IaAlertaModulo;
  /** A entidade afetada; nos alertas agregados, a data de referencia. */
  chave: string;
  severidade: IaAlertaSeveridade;
  titulo: string;
  dados: Record<string, any>;
  link?: string | null;
  estado: IaAlertaEstado;
  motivo_baixa?: string | null;
  detectado_em: string;
  notificado_em?: string | null;
  baixado_em?: string | null;
  baixado_por?: string | null;
}

export interface VickMessage {
  id?: number;
  role: 'user' | 'assistant';
  content: string;
  sources?: VickSource[];
}

export interface VickConversation {
  id: number;
  titulo?: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Conta bancária do parceiro/vendedor_parceiro (múltiplas por usuário, uma "principal").
 * Vive em tabela própria (contas_bancarias) com campos sensíveis criptografados — só é
 * lida/gravada via a Edge Function manage-bank-accounts, nunca por select('*') direto.
 * Este é o DTO já decriptado retornado pela função (action: "list").
 */
export interface ContaBancaria {
  id: string;
  banco: string;
  agencia: string;
  conta_corrente: string;
  tipo_conta: 'corrente' | 'poupanca';
  titular_nome: string;
  titular_cpf_cnpj: string;
  principal: boolean;
  created_at: string;
}

export type BoletoStatus = 'emitido' | 'enviado' | 'pago' | 'vencido' | 'cancelado';

export interface Fatura {
  id: string;
  created_at: string;
  uc_codigo: string;
  /** Coluna GERADA (uc_codigo sem . - espaço e zeros à esquerda). Somente leitura — nunca enviar num insert/update. Use para casar com unidades_consumidoras via ucCodigoKeys(). */
  uc_codigo_norm?: string | null;
  cliente_nome: string;
  cliente_cpf_cnpj: string;
  tipo_ligacao: string;
  mes_referencia: string;
  data_vencimento: string;
  valor_fatura_distribuidora: number;
  consumo_ativo_kwh: number;
  valor_economia_gd: number;
  valor_tarifa_distribuidora?: number;
  status_boleto_vigor: string;
  pdf_fatura?: string;
  tipo_pdf_fatura?: 'espelho' | 'definitiva' | null;
  pdf_boleto_combinado?: string | null;
  /** URL publica e permanente do mesmo PDF, hospedado no site institucional
   * (`/docs/boletos/<uc>/<arquivo>.pdf`, Vercel Blob). E o link que deve
   * circular: o `pdf_boleto_combinado` vive em bucket privado e so abre por
   * URL assinada, que expira e carrega o token do Storage na propria URL.
   * Preenchida por `gerar-boleto-gran`; ver src/lib/linkBoleto.ts. */
  url_boleto_combinado?: string | null;
  /** Token opaco que serve o pdf_boleto_combinado sem login, via edge function
   * `boleto-publico`. Carimbado por trigger quando o PDF combinado é gerado; é
   * o que monta o link do botão no template WhatsApp. */
  token_boleto_publico?: string | null;
  // GD fields
  energia_injetada_gd1_kwh?: number | null;
  energia_injetada_gd2_kwh?: number;
  tarifa_injecao_gd1?: number;
  tarifa_injecao_gd2?: number;
  tarifa_parc_injet_s_desc_gd2?: number;
  tarifa_bandeira?: number;
  ilum_publica?: number | null;
  outros_multas?: number | null;
  valor_parcelamentos_boleto?: number | null;
  valor_juros_boleto?: number | null;
  cadastro_rateio?: number;
  // Tax fields
  icms?: number;
  pis?: number;
  cofins?: number;
  classificacao?: string;
  status_fatura?: string;
  data_apresentacao?: string;
  leitura_anterior?: string | null;
  leitura_atual?: string | null;
  proxima_leitura?: string | null;
  geracao_ciclo?: string | null;
  // Unidades Geradoras
  unidade_geradora_1?: string | null;
  unidade_geradora_2?: string | null;
  unidade_geradora_3?: string | null;
  unidade_geradora_4?: string | null;
  unidade_geradora_5?: string | null;
  geracao_unidade_geradora_1?: number | null;
  geracao_unidade_geradora_2?: number | null;
  geracao_unidade_geradora_3?: number | null;
  geracao_unidade_geradora_4?: number | null;
  geracao_unidade_geradora_5?: number | null;
  // Validation fields
  valor_consumo_nao_comp?: number;
  valor_adc_bandeira?: number;
  valor_parc_injet_s_desc?: number;
  valor_restituicao_imposto?: number;
  consumo_scee_tarifa_c_imposto?: number;
  energia_nao_isenta?: number;
  // Boleto Calculation fields
  desconto_tarifa?: number;
  desconto_bandeira?: number;
  valor_consumo_scee_boleto?: number;
  valor_adc_bandeira_boleto?: number;
  valor_parc_injet_s_desc_boleto?: number;
  valor_restituicao_imposto_boleto?: number;
  valor_energia_nao_isenta_boleto?: number;
  valor_boleto_vigor?: number;
  valor_pagamento?: number | null;
  url_boleto?: string;
  codigo_pix_boleto?: string;
  linha_digitavel_boleto?: string;
  num_cobranca_boleto?: string;
  /** Conta Sicoob em que o boleto EXISTE, carimbada pela linha digitável. É a que vale
   *  para baixa, prorrogação e consulta — nunca deduzir pela UC. */
  conta_sicoob?: ContaSicoob | null;
  /** Ambiente em que a cobrança nasceu. 'sandbox' = emissão de teste: fica fora de
   *  todo relatório e só é visível para adm (RLS + filtro das listagens). */
  ambiente?: 'producao' | 'sandbox' | null;
  // Boleto Emission fields
  consumo_total_gd_s_vigor?: number;
  valor_adc_bandeira_s_vigor?: number;
  custo_s_vigor?: number;
  economia?: number;
  outros_multas_boleto?: number | null;
  outros_multas_descricao?: string | null;
  comprovante_pagamento_url?: string | null;
  
  // New Info Fields for Boleto
  nome_cliente_boleto?: string;
  cpf_cnpj_cliente_boleto?: string;
  end_logradouro?: string;
  end_numero?: string;
  end_bairro?: string;
  end_cep?: string;
  end_cidade?: string;
  end_uf?: string;
  vencimento_boleto?: string;
  data_limite_pagamento_boleto?: string;
  enviado_por_nome?: string;
  enviado_em?: string;
  // Envio da cobrança por e-mail (Resend) — escrito só pela edge function
  // enviar-cobranca-email. NULL em email_cobranca_status = nunca houve tentativa.
  email_cobranca_status?: 'enviado' | 'falhou' | null;
  email_cobranca_em?: string | null;
  email_cobranca_destinatarios?: string[] | null;
  email_cobranca_anexos?: string[] | null;
  email_cobranca_erro?: string | null;
  email_cobranca_message_id?: string | null;
  qtd_injecao_scee_boleto?: number;
  qtd_adc_bandeira_boleto?: number;
  qtd_parc_injet_boleto?: number;
  parcelamento_descricao?: string | null;
  /** Avulsa de saldo remanescente; filtrada pelo card "Saldo Remanescente". */
  saldo_remanescente?: boolean;
  parcelamento_origem_id?: string | null;
  /** Troca de dono da UC: preenchido = a fatura é do dono ANTERIOR, não do cadastro
   *  atual da UC (uc_trocas_dono.negociacao_origem_id). Ver src/lib/trocaDonoUc.ts. */
  dono_anterior_troca_id?: string | null;
  quitado_em?: string | null;
  quitado_por_nome?: string | null;
  quitacao_observacao?: string | null;
  observacoes_boleto?: string | null;
  emitido_por_nome?: string;
  emitido_em?: string;
  cancelado_por_nome?: string;
  cancelado_em?: string;
  motivo_cancelamento_boleto?: string;
  historico_cancelamentos?: {
    cancelado_por_nome: string;
    cancelado_em: string;
    motivo_cancelamento_boleto: string;
    /**
     * Qual boleto foi baixado. Opcional porque as entradas anteriores a 21/09/2026
     * não o têm: o campo da fatura é zerado no mesmo update do cancelamento, então
     * até aqui o número sumia junto e auditar boleto órfão era inferência.
     */
    num_cobranca_boleto?: string | null;
    /** now() do servidor — posto por trg_faturas_carimbo_cancelamento. */
    registrado_em?: string | null;
  }[];
  /**
   * Uma entrada por emissão, append-only, gravada por trg_faturas_historico_emissoes.
   *
   * `emitido_por_nome`/`emitido_em` acima descrevem o boleto VIVO e são sobrescritos
   * na reemissão (decisão de 19/08/2026, que export, Kanban e KPIs dependem). Este
   * array é onde fica quem emitiu antes — ver src/lib/timelineCobranca.ts.
   */
  historico_emissoes?: {
    emitido_por_nome: string;
    emitido_em: string;
    num_cobranca_boleto?: string | null;
    registrado_em?: string | null;
    origem?: string | null;
    /** Motivo/justificativa da emissão fora do prazo, quando houve. Ver `motivo_atraso_emissao`. */
    motivo_atraso_emissao?: string | null;
    justificativa_atraso_emissao?: string | null;
  }[];
  /**
   * Motivo da emissão fora do prazo padrão de faturamento — lista fechada em
   * src/lib/janelaFaturamento.ts. Nulo quando a emissão foi no prazo, é boleto
   * avulso ou é reemissão. Como `emitido_por_nome`, descreve a emissão VIVA; o
   * registro por emissão fica em `historico_emissoes`.
   */
  motivo_atraso_emissao?: string | null;
  /** Texto livre do motivo. Obrigatório só quando o motivo é "Outro". */
  justificativa_atraso_emissao?: string | null;
  // Technical / Generation fields
  excedente_recebido_unidade_geradora_1?: number | null;
  excedente_recebido_unidade_geradora_2?: number | null;
  excedente_recebido_unidade_geradora_3?: number | null;
  excedente_recebido_unidade_geradora_4?: number | null;
  excedente_recebido_unidade_geradora_5?: number | null;
  credito_recebido?: number | null;
  saldo_acumulado_kwh?: number | null;
  saldo_ugs_vigor?: number | null;
  saldo_ugs_outros?: number | null;
  /** Confirmação dos saldos acima na validação (FaturaValidationModal). NULL antes de 21/09/2026. */
  saldo_conferido_em?: string | null;
  saldo_conferido_por_nome?: string | null;
  // Generator details
  descricao_ug_1?: string | null;
  descricao_ug_2?: string | null;
  descricao_ug_3?: string | null;
  descricao_ug_4?: string | null;
  descricao_ug_5?: string | null;
  injecao_kwh_ug_1?: number | null;
  injecao_kwh_ug_2?: number | null;
  injecao_kwh_ug_3?: number | null;
  injecao_kwh_ug_4?: number | null;
  injecao_kwh_ug_5?: number | null;
  // Audit Soft Delete
  excluido_por_nome?: string;
  excluido_em?: string;
  motivo_exclusao?: string;
  duplicata_confirmada?: boolean;
  bloqueado_envio_duplicata?: boolean;
  /** Por que a segunda fatura do mesmo mês/UC foi mantida (autorização de duplicata). */
  duplicata_motivo?: string | null;
  duplicata_confirmada_por_nome?: string | null;
  duplicata_confirmada_em?: string | null;
  total_com_vigor?: number;
  // Skip Calculation fields
  pular_calculo_por_nome?: string;
  pular_calculo_em?: string;
  motivo_pular_calculo?: string;
  /** Lista fechada em src/lib/motivoPularCalculo.ts. NULL antes de 15/09/2026. */
  motivo_pular_calculo_codigo?: string | null;
  /** Pulo feito sob a regra de injeção (07/10/2026). Formato: DetalhePulo em src/lib/pularCalculoInjecao.ts. */
  pular_calculo_detalhe?: Record<string, unknown> | null;
  /** Pulos desfeitos ou refeitos, gravados pelo gatilho fn_limpar_pulo_desfeito. */
  historico_pulos?: Record<string, unknown>[] | null;
  // Skip Send fields
  motivo_pular_envio?: string;
  pular_envio_por_nome?: string;
  pular_envio_em?: string;
  // Kanban & Distribution
  data_cobranca_agendada?: string | null;
  responsavel_cobranca_id?: string | null;
  // Reagendamento Audit fields
  motivo_reagendamento?: string | null;
  comprovante_reagendamento_url?: string | null;
  reagendamento_por_nome?: string | null;
  reagendamento_em?: string | null;
  // Meter
  numero_medidor?: string | null;
  // Reaviso de Vencimento (corte iminente)
  tem_reaviso_vencimento?: boolean | null;
  reaviso_vencimento_texto?: string | null;
  // Empresa Parceira (NULL = fatura da Vigor Energy)
  empresa_parceira_id?: string | null;
  // Data de pagamento do boleto (preenchida pelo webhook do banco)
  data_pgto_boleto?: string | null;
  // Auditoria de Pagamentos (comissão variável por boleto pago)
  auditado_pagamento?: boolean;
  auditado_pagamento_em?: string | null;
  auditado_pagamento_por_id?: string | null;
  auditado_pagamento_por_nome?: string | null;
  // Lote de Nota Fiscal (notas_fiscais_auditoria_pagamento) que confirmou a
  // auditoria deste boleto — NULL enquanto auditado_pagamento=false.
  auditado_pagamento_nf_id?: string | null;
  auditado_pagamento_nf?: NotaFiscalAuditoriaPagamento | null;
  // Boleto único (Vigor + Equatorial) — dados da PRÓPRIA fatura da Equatorial,
  // usados pelo repasse automático (ver mapeador-faturas e trg_enfileirar_pagamento_equatorial).
  linha_digitavel_equatorial?: string | null;
  pix_copia_cola_equatorial?: string | null;
  debito_automatico_equatorial?: boolean | null;
  // Marcado na emissão (BoletoEmissionModal): o cliente já pagou a fatura da
  // Equatorial por fora, então o boleto único cobra só a parte da Vigor, o split
  // não retém nada para a parceira a título de distribuidora e o repasse não é
  // enfileirado. Não confundir com debito_automatico_equatorial (a distribuidora
  // debita sozinha) — aqui a conta daquele mês já está paga.
  equatorial_quitada_pelo_cliente?: boolean | null;
  // Cliente PRÓPRIO da empresa parceira, fora da parceria com a Vigor (boleto avulso
  // da Gran): sai sem split, a Equatorial é paga pela conta da parceira e o resto fica
  // com ela. Fora da régua, da fila de cobrança humana e dos números da Vigor.
  fora_parceria_vigor?: boolean;
  valor_emissao_boleto_unico?: number | null;
  // Boleto único (UC da Vigor, Sicoob): quanto da conta da Equatorial o boleto emitido
  // cobra. NULL = não cobra. Calculado pelo banco quando a linha digitável chega
  // (trg_d_marca_equatorial_no_boleto) — o app só lê. Só com ele o pagamento do cliente
  // põe a Equatorial na fila de pagamento.
  valor_equatorial_no_boleto?: number | null;
  // Espelha o CHECK faturas_status_repasse_equatorial_check. 'manual_pendente' = a UC está
  // com repasse manual (unidades_consumidoras.repasse_equatorial_automatico = false), então
  // quem quita a distribuidora é a empresa parceira e a baixa é humana, em
  // /financeiro/repasse-equatorial-manual.
  status_repasse_equatorial?:
    | 'nao_aplicavel'
    | 'pendente'
    | 'aguardando_confirmacao'
    | 'pago'
    | 'falhou'
    | 'dados_indisponiveis'
    | 'reembolsado'
    | 'uc_nao_encontrada'
    | 'debito_automatico'
    | 'manual_pendente'
    | 'quitado_pelo_cliente'
    | null;
  // Empresa parceira, cobranças emitidas na conta da PARCEIRA (até 26/09/2026):
  // fixedValue que o Asaas de fato transferiu para a carteira da Vigor no split —
  // é o valor "destinado à Vigor Energy" a considerar na Auditoria de Pagamentos
  // para essas faturas. NULL quando a cobrança nasceu na conta da Vigor.
  split_repasse_vigor?: number | null;
  // Divisão econômica congelada na emissão: residual da parceira e parte da Vigor
  // (energia + bandeira pelo % da UC). Ver _shared/splitParceira.ts.
  split_valor_parceira?: number | null;
  split_valor_vigor?: number | null;
  // Conta Asaas em que a cobrança nasceu. 'vigor' desde 26/09/2026; NULL em
  // fatura de parceira = legado = conta da parceira.
  conta_asaas_emissora?: 'vigor' | 'parceira' | null;
  // % da Vigor da UC congelado na emissão (o da UC pode mudar depois).
  percentual_vigor_aplicado?: number | null;
  // fixedValue do split para a carteira da parceira quando a Vigor emite:
  // residual − taxa do Asaas. Ver supabase/functions/_shared/splitParceira.ts.
  split_repasse_parceira?: number | null;
}

/**
 * Fila de repasses à Equatorial (boleto único) — ver pagamento_equatorial_fila.
 *
 * Os estados se dividem em duas trilhas que nunca se cruzam:
 *  - automática: 'pendente' → 'processando' → 'aguardando_confirmacao' → 'concluido' | 'falhou',
 *    tocada pelo cron (claim_pagamento_equatorial_fila só reivindica 'pendente');
 *  - manual: 'manual' → 'pago_manual', tocada só por gente, pela RPC
 *    dar_baixa_repasse_equatorial. O cron nunca enxerga esses itens.
 */
export interface PagamentoEquatorialFila {
  id: string;
  fatura_id: number;
  uc_codigo: string;
  mes_referencia: string;
  valor_equatorial: number;
  linha_digitavel: string | null;
  pix_copia_cola: string | null;
  webhook_url: string | null;
  gateway: 'sicoob' | 'asaas';
  ambiente: 'sandbox' | 'producao';
  empresa_parceira_id: string | null;
  status: 'pendente' | 'processando' | 'aguardando_confirmacao' | 'concluido' | 'falhou' | 'manual' | 'pago_manual';
  tentativas: number;
  asaas_bill_id: string | null;
  asaas_bill_status: string | null;
  // Chave do pagamento no Sicoob (x-idempotency-key). Repetir com ela não paga de novo.
  sicoob_idempotency_key?: string | null;
  error_details: string | null;
  resultado: Record<string, any> | null;
  // Conta Asaas que paga (gateway asaas); NULL no Sicoob, que paga pela conta da fatura.
  conta_pagadora?: 'vigor' | 'parceira' | null;
  // Item adiado (horário bancário, saldo, recusa): quando a fila tenta de novo.
  proxima_tentativa_em?: string | null;
  // Preenchidos só na baixa manual, pela RPC dar_baixa_repasse_equatorial.
  baixa_em: string | null;
  baixa_por_nome: string | null;
  baixa_observacao: string | null;
  created_at: string;
  updated_at: string;
}

// Histórico append-only de cada tentativa de repasse à Equatorial (boleto único)
// — uma linha por tentativa, sucesso ou falha. Ver pagamento_equatorial_auditoria.
export interface PagamentoEquatorialAuditoria {
  id: string;
  fila_id: string | null;
  fatura_id: string;
  uc_codigo: string;
  mes_referencia: string;
  cliente_nome: string | null;
  tentativa_numero: number;
  valor_equatorial: number;
  linha_digitavel: string | null;
  pix_copia_cola: string | null;
  webhook_url: string | null;
  gateway: 'sicoob' | 'asaas';
  asaas_bill_id?: string | null;
  consulta_status: boolean;
  request_payload: Record<string, any> | null;
  response_status: number | null;
  response_body: Record<string, any> | null;
  // true = pago. false = falha definitiva (consome tentativa). null = submetido
  // ao gateway (Asaas) e aguardando confirmação assíncrona — não é falha.
  sucesso: boolean | null;
  error_details: string | null;
  executado_em: string;
}

// Nota Fiscal de um lote de Auditoria de Pagamentos — uma linha por AÇÃO de
// confirmação (1 ou N boletos marcados como auditados de uma vez), não por
// boleto individual. Ver faturas.auditado_pagamento_nf_id.
export interface NotaFiscalAuditoriaPagamento {
  id: string;
  numero_nf: string;
  valor_nf: number;
  anexo_url: string;
  anexo_nome: string | null;
  criada_em: string;
  criada_por_id: string | null;
  criada_por_nome: string | null;
}

/** Credenciais do gateway de pagamento da própria Vigor Energy (hoje: Asaas) — ver views/Config/VigorGatewayAsaasView.tsx. */
export interface VigorGatewayBanco {
  id: string;
  banco: 'asaas';
  client_id?: string | null;
  ambiente: 'sandbox' | 'producao';
  created_at: string;
  updated_at: string;
}

export interface MapaUnidadeData {
  cidade: string;
  estado: string;
  total_ucs: number;
  latitude: number | null;
  longitude: number | null;
  unidades?: {
    numero_uc: string;
    outro_numero_uc?: string;
    nome_cliente: string;
  }[];
}

export interface BoletoVigor {
  id: string;
  fatura_id: string;
  valor_total: number;
  valor_desconto: number;
  valor_tarifa_distribuidora?: number;
  data_emissao: string;
  data_vencimento: string;
  status: BoletoStatus;
  pdf_boleto_url?: string;
  codigo_barras: string;
  created_at: string;
}

export interface Pipeline {
  id: string;
  name: string;
  description?: string;
  created_at: string;
}

export interface PipelineStage {
  id: string;
  pipeline_id: string;
  name: string;
  position: number;
  color?: string;
  created_at: string;
}

export type NegociacaoStage = string; // Dynamic now
export type NegociacaoStatus = 'em_andamento' | 'simulacao_realizada' | 'termo_enviado' | 'termo_assinado' | 'termo_analise' | 'termo_aprovado' | 'termo_reprovado' | 'ganho' | 'perdido' | 'em_pausa';

export interface Negociacao {
  id: number;
  user_id?: string;
  titulo: string;
  cliente_nome: string;
  numero_uc?: string;
  responsavel_id?: string;
  stage: NegociacaoStage;
  status: NegociacaoStatus;
  analise_cadastral?: any[];
  avalistas?: NegociacaoAvalista[];

  consumo_medio_kwh: number;
  avaliacao_estrelas: number;
  origem?: string;
  /** Atribuição do lead (ex.: preenchido automaticamente para leads do Meta Ads). */
  plataforma?: string;
  campanha?: string;
  anuncio?: string;
  formulario?: string;
  /** Resposta da pergunta de qualificação "Você reside no estado de Goiás?" (leads do Meta Ads). */
  reside_goias?: boolean | null;
  /** Faixa de conta de luz informada no formulário (leads do Meta Ads): "menos_de_r$300" | "de_r$300_a_r$700" | "acima_de_r$700". */
  valor_conta_luz?: string | null;
  /** lead_id do Meta (Lead Ads) — chave de match pro Conversions API reportar conversões (ganho) de volta pro anúncio de origem. */
  meta_lead_id?: string | null;
  pipeline_id?: string;
  // Kanban "Meu Trabalho Hoje" (Bruno)
  favorito?: boolean;               // estrela "Prioridade do dia" → chip Favoritos
  proxima_acao?: string | null;     // override manual da Próxima Ação (NULL = derivada do status)
  score_prioridade?: 'P1' | 'P2' | 'P3' | null; // override manual do score (NULL = calculado)
  // New Fields
  tipo_cliente?: 'PF' | 'PJ';
  cpf_cnpj?: string;
  whatsapp?: string;
  email?: string;
  // Contatos do financeiro no Quadro Resumo do termo. Opcionais — cada campo vazio
  // sai no termo com o dado principal do cliente (nome / e-mail / whatsapp).
  contato_financeiro?: string;
  email_financeiro?: string;
  whatsapp_financeiro?: string;
  previsao_fechamento?: string;
  tags?: string[];
  fidelidade?: 'Sem fidelidade' | '12 meses' | '18 meses' | '24 meses' | '36 meses';
  // Nova Política Comercial (jul/2026) — snapshot gravado pelo modal Alterar Plano
  /** Modelo de plano da negociação, carimbado na criação pelo cargo de quem criou —
   *  define qual formulário todo mundo vê/grava, independente de quem abre. */
  plano_modalidade?: 'parceiro' | 'vendedor' | null;
  /** BDR que trabalhou a negociação antes de passá-la adiante (gatilho trg_negociacao_carimba_bdr).
   *  Base da futura comissão do BDR por negociação fechada pela Closer. */
  bdr_id?: string | null;
  bdr_passou_em?: string | null;
  plano_codigo?: string | null;
  publico?: 'A' | 'B' | 'C' | null;
  multiplicador_plano?: number | null;
  fator_fidelidade?: number | null;
  multiplicador_final?: number | null;
  nome_representante_legal?: string;
  cpf_representante?: string;
  contrato_social_data_versao?: string | null; // Documento societário base (Quadro Resumo PJ do termo rev V)
  data_nascimento_cliente?: string;
  data_nascimento_representante?: string; // PJ: nascimento do representante da associação (análise de crédito)
  stage_changed_at?: string;
  documentos_json?: { url: string; name: string }[] | null;
  pasta_sharepoint_url?: string;
  desconto_status?: 'aprovado' | 'pendente' | 'rejeitado';
  /** Fluxo de Troca de Titularidade durante a Análise Documental (ver AnaliseCadastral.necessita_troca_titularidade). */
  troca_titularidade_status?: 'pendente_envio' | 'documento_enviado' | 'aguardando_reanalise' | null;
  motivo_perda?: string;
  observacoes_perda?: string;
  notas?: string;
  observacao?: string;
  desconto_adicional?: string;
  url_simulacao?: {
    url_pdf: string;
    data_criacao: string;
    valor_mensal?: string;
    valor_economia?: string;
    numero_simulacao?: string;
  }[] | null;
  url_termo?: {
    url_pdf: string;
    data_criacao: string;
    tipo_cliente?: string;
    numero_termo?: string;
    /** ID completo do documento no Autentique — usado p/ liberar assinaturas internas pós-aprovação. */
    id_autentique?: string;
    /** Procurador + testemunhas escolhidos na emissão; assinam após a aprovação da análise cadastral. */
    signatarios?: {
      procurador?: { nome: string; cpf?: string; email: string } | null;
      testemunha1?: { nome: string; cpf?: string; email: string } | null;
      testemunha2?: { nome: string; cpf?: string; email: string } | null;
    };
    /** Assinaturas internas já liberadas no Autentique (pós-aprovação da análise cadastral). */
    assinaturas_liberadas_em?: string;
    /** Marcado pelo admin como o termo efetivamente assinado/válido (override do duplo envio). */
    validado?: boolean;
    /** Quando o cliente assinou este termo no Autentique (ISO). Preenchido pela
     * sincronização (reconciliar-termo-adesao) ou pelo admin ao validar. */
    data_assinatura?: string;
    /** false = assinou, mas a biometria (SERPRO) ainda não voltou: o Autentique
     * mostra "Aguardando verificação" e não gera o PDF assinado. Ausente nos termos
     * anteriores à sincronização por termo (08/2026). */
    assinatura_verificada?: boolean;
    /** Quando a sincronização conferiu este termo contra o Autentique (ISO). */
    assinatura_sincronizada_em?: string;
    /** Simulação (Plano de Benefícios) anexada ao Quadro Resumo deste termo — escolhida pelo usuário na emissão. */
    numero_simulacao?: string;
    link_simulacao?: string;
    data_simulacao?: string;
  }[] | null;
  termo_link_do_cliente?: string | null;

  analise_cadastral_started_at?: string;
  data_assinatura_termo?: string;
  analise_cadastral_minutos_realizados?: number;

  // GeoIntelligence (VigorGeo)
  segmento?: string;
  cidade?: string;
  bairro?: string;
  latitude?: number;
  longitude?: number;
  geocode_status?: 'pendente' | 'endereco' | 'cidade' | 'falhou';
  ultima_atividade_at?: string;

  created_at: string;
  updated_at: string;
  tarefas?: {
    id: string;
    status: string;
    vencimento: string;
  }[];
  unidades_consumidoras?: {
    numero_uc: string;
    cidade?: string;
  }[];
  responsavel?: Profile;
}

/** Linha da view vw_mapa_leads (Mapa Inteligente do CRM). */
export interface MapaLead {
  id: number;
  titulo: string;
  cliente_nome: string;
  stage: string;
  status: NegociacaoStatus;
  segmento?: string | null;
  cidade?: string | null;
  bairro?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  geocode_status: 'pendente' | 'endereco' | 'cidade' | 'falhou';
  consumo_medio_kwh: number;
  avaliacao_estrelas: number;
  responsavel_id?: string | null;
  pipeline_id?: string | null;
  whatsapp?: string | null;
  created_at: string;
  ultima_atividade_at: string;
  dias_sem_atividade: number;
  congelado: boolean;
  docs_count: number;
}

export interface NegociacaoNota {
  id: number;
  negociacao_id: number;
  user_id: string;
  conteudo: string;
  tipo: 'nota' | 'email' | 'whatsapp';
  created_at: string;
  user?: Profile;
  arquivo_url?: string;
  arquivo_nome?: string;
  arquivo_tipo?: string;
}

export interface NegociacaoLog {
  id: number;
  negociacao_id: number;
  user_id: string;
  acao: string; // e.g., 'criou', 'atualizou'
  detalhes: string;
  created_at: string;
  user?: Profile;
}

// Caminho de recuperação de uma negociação reprovada na análise cadastral —
// ver supabase/migrations/20260729190010_create_negociacao_avalistas.sql
export type NegociacaoAvalistaStatus = 'cadastrado' | 'termo_enviado' | 'assinaturas_concluidas' | 'analise_reenviada' | 'removido';

export interface NegociacaoAvalista {
  id: number;
  negociacao_id: number;
  nome_completo: string;
  cpf: string;
  email?: string;
  telefone?: string;
  /** Endereço completo, digitado no cadastro — sai no Termo de Anuência (#6368). */
  endereco?: string | null;
  status: NegociacaoAvalistaStatus;
  id_termo_autentique?: string;
  termo_url_pdf?: string;
  /** Link curto de assinatura do titular no Autentique. Nulo em termos gerados
   * antes de 31/07/2026 — nesses a tela ainda cai no fallback de negociacao_logs. */
  link_titular?: string;
  /** Link curto de assinatura do avalista — é o que exige validação facial no
   * CPF DELE. Nunca entregar este link ao titular. */
  link_avalista?: string;
  titular_assinou_em?: string;
  /** Só é preenchido quando a validação facial (SERPRO) do avalista foi APROVADA
   * — é o que os gates de reenvio para análise consideram como assinatura válida. */
  avalista_assinou_em?: string;
  /** Avalista assinou, mas o SERPRO ainda não retornou a validação facial. */
  avalista_assinatura_pendente_em?: string;
  /** SERPRO reprovou a validação facial: a assinatura não vale, precisa novo termo. */
  avalista_biometria_reprovada_em?: string;
  consulta_credito_json?: any;
  created_at: string;
  updated_at: string;
}

// Declaração de não associação: consumidor reprovado por crédito recebe atestado,
// assinado só pela Associação, de que não é associado. Emitir encerra a negociação.
// Ver supabase/migrations/20261016120000_declaracao_nao_associacao.sql
export interface DeclaracaoNaoAssociacao {
  id: number;
  negociacao_id: number;
  /** Cópia do que foi impresso — o cadastro pode mudar depois. */
  cliente_nome: string;
  cpf_cnpj: string;
  ucs: string[];
  termo_assinado_em: string | null;
  procurador_nome: string;
  id_autentique: string | null;
  nome_documento: string | null;
  /** Nulo = criada, mas a assinatura do procurador ainda não voltou. */
  assinado_em: string | null;
  url_pdf_assinado: string | null;
  emitido_por: string | null;
  created_at: string;
  /** Embed `emissor:profiles(full_name)`. */
  emissor?: { full_name: string | null } | null;
}

// ── Escala/disponibilidade do Comercial Interno + SLA de atendimento ────────
// (supabase/migrations/20260825120000_escala_comercial_interno.sql)

/** Jornada de um dia da semana para um usuário. 0=domingo .. 6=sábado (extract(dow) do Postgres). */
export interface HorarioTrabalho {
  id: string;
  profile_id: string;
  dia_semana: number;
  ativo: boolean;
  hora_inicio: string; // 'HH:MM' ou 'HH:MM:SS'
  hora_fim: string;
  almoco_inicio?: string | null;
  almoco_fim?: string | null;
  updated_at: string;
}

/** Ausência pontual — só gestor/admin grava diretamente (ver SolicitacaoAusencia
 *  para o vendedor, e origem='pausa' para a pausa de SLA que a própria pessoa dá
 *  pelo botão do topo, via fn_pausar_sla/fn_retomar_sla). */
export interface AusenciaTrabalho {
  id: string;
  profile_id: string;
  data: string; // 'YYYY-MM-DD'
  dia_inteiro: boolean;
  hora_inicio?: string | null;
  hora_fim?: string | null;
  motivo?: string | null;
  criado_por?: string | null;
  origem: 'gestor' | 'solicitacao' | 'pausa';
  created_at: string;
}

export type SolicitacaoAusenciaStatus = 'pendente' | 'aprovada' | 'rejeitada';

/** Pedido de ausência de última hora feito pelo próprio usuário — vira AusenciaTrabalho quando aprovado. */
export interface SolicitacaoAusencia {
  id: string;
  profile_id: string;
  data: string;
  dia_inteiro: boolean;
  hora_inicio?: string | null;
  hora_fim?: string | null;
  motivo?: string | null;
  status: SolicitacaoAusenciaStatus;
  decidido_por?: string | null;
  decidido_em?: string | null;
  created_at: string;
  profile?: Profile;
}

/**
 * Um período de responsabilidade sobre uma negociação — histórico completo,
 * não só o responsável atual, porque o SLA reseta a cada transferência.
 * `primeira_atividade_em` é o que fecha o relógio do SLA (nota, mudança de
 * status/etapa manual ou transferência — nunca ação automática do sistema).
 */
export interface NegociacaoAtribuicao {
  id: string;
  negociacao_id: number;
  responsavel_id: string;
  atribuido_em: string;
  encerrado_em?: string | null;
  primeira_atividade_em?: string | null;
  tipo_atividade?: string | null;
  caiu_por_indisponibilidade: boolean;
  /** Correção manual (adm/gerente): exclui esta atribuição da média geral e do
   *  ranking individual de SLA — ex.: teste que gerou um tempo atípico. */
  sla_desconsiderado: boolean;
  sla_desconsiderado_motivo?: string | null;
  sla_desconsiderado_por?: string | null;
  sla_desconsiderado_em?: string | null;
}

export type TarefaStatus = 'a_iniciar' | 'em_andamento' | 'aguardando' | 'concluido' | 'cancelado';
export type TarefaPrioridade = 'baixa' | 'media' | 'alta' | 'urgencia';

// Solicitação (chamado interno — TI ou Marketing) — categoria irmã de tarefa comum, distinta de
// Visita (tarefas.tipo). "Central de Solicitações" no menu; "Tarefas e Visitas" segue sendo o
// módulo comercial (tarefas + visitas de vendedor), sem relação com isto.
export type TarefaCategoria = 'tarefa' | 'solicitacao';
// Departamento dono da fila — cada um tem seu próprio vocabulário de tipo_solicitacao (ver
// views/Tarefas/solicitacaoTipos.ts). Espelha o CHECK tarefas_departamento_solicitacao_check
// (migração 20260807120000).
// Tecnologia e Marketing formam uma equipe só (com tipos); as outras três são equipes sem tipo (#6518).
export type SolicitacaoDepartamento = 'Tecnologia' | 'Marketing' | 'Financeiro' | 'Faturamento' | 'Comercial' | 'Suporte técnico';
// Rótulos, cores e agrupamento em views/Tarefas/solicitacaoTipos.ts; espelha o CHECK
// tarefas_tipo_solicitacao_check (migração 20260807120000 — expandiu p/ incluir Marketing).
export type SolicitacaoTipo =
  // Tecnologia
  | 'bug' | 'correcao_dados'
  | 'melhoria' | 'edicao_cadastro' | 'sugestao'
  | 'duvida_suporte' | 'acesso_permissao' | 'relatorio_exportacao' | 'outro_tecnologia'
  | 'integracao' | 'infraestrutura'
  // Marketing
  | 'peca_grafica' | 'video_reels' | 'copywriting'
  | 'campanha_anuncio' | 'post_social' | 'landing_page'
  | 'relatorio_metricas' | 'outro';
export type SolicitacaoStatus = 'novo' | 'em_analise' | 'em_desenvolvimento' | 'em_teste' | 'concluido' | 'rejeitado';

// ── Visita de pós-venda (spec Bruno) — tarefas.tipo = 'pos_venda' ──
export type PosVendaOrigem = 'sugestao_sistema' | 'atribuicao_manual';
export type PosVendaResultado =
  | 'satisfeito' | 'duvida_tecnica' | 'oportunidade_upsell' | 'reclamacao' | 'cliente_ausente';

export const POS_VENDA_RESULTADO_LABELS: Record<PosVendaResultado, string> = {
  satisfeito: 'Satisfeito',
  duvida_tecnica: 'Dúvida técnica',
  oportunidade_upsell: 'Oportunidade de upsell',
  reclamacao: 'Reclamação',
  cliente_ausente: 'Cliente ausente',
};

/**
 * Linha de `vw_pos_venda_sugestoes` (migration 20260804120000).
 * `adimplente` é DERIVADO de faturas (boleto Vigor vencido) — não do campo manual
 * unidades_consumidoras.status_financeiro, que nasce 'Não se aplica' e ninguém preenche.
 */
export interface PosVendaSugestao {
  negociacao_id: number;
  cliente_nome: string;
  cidade: string | null;
  bairro: string | null;
  responsavel_id: string | null;
  consumo_medio_kwh: number | null;
  ultima_atividade: string;
  dias_sem_contato: number;
  adimplente: boolean;
  latitude: number | null;
  longitude: number | null;
  /**
   * pendente | endereco | cidade | falhou. Leia SEMPRE junto de latitude/longitude:
   * 'cidade' é o centro do município com deslocamento aleatório de ~600 m, não o
   * endereço do cliente — ordenar por distância com isso é ruído dentro da cidade.
   */
  geocode_status: string | null;
}

export interface Tarefa {
  id: string; // or number, handled as string in frontend usually fine if backend sends number
  /** Marcas livres da tarefa. 'vick' = aberta pela Vick, sem solicitante (#6597); 'inbox' = do Inbox. */
  tags?: string[] | null;
  titulo: string;
  descricao: string;
  // Opcional só nasce assim pra Solicitação (fila sem dono) — quem pega a demanda se atribui via "Transferir"
  responsavel_id?: string | null;
  solicitante_id?: string;
  recorrencia?: string; // e.g., 'diaria', 'semanal', 'mensal'
  recorrencia_fim?: string;
  status: TarefaStatus;
  prioridade: TarefaPrioridade;
  vencimento: string;
  data_conclusao?: string | null;
  anexos_url?: string[];
  checklists?: { id: string; text: string; completed: boolean }[];

  // Visita comercial (Rotas v2) — NULL = tarefa comum
  tipo?: string | null; // prospeccao | qualificacao | simulacao | apresentacao | negociacao | retorno | pos_venda | visita
  objetivo?: string | null;
  hora_visita?: string | null;
  modalidade?: Modalidade; // tipo de visita: presencial entra na rota geográfica; remota = "Remotas do dia"

  // Visita de pós-venda (tipo = 'pos_venda') — atribuída pelo gestor na Central de Sugestões
  origem_atribuicao?: PosVendaOrigem | null;
  atribuido_por?: string | null; // profiles.id do gestor que atribuiu
  motivo_sugestao?: string | null; // congelado no momento da atribuição, ex.: "42 dias sem contato"
  resultado_categoria?: PosVendaResultado[] | null; // opcional na conclusão; a observação continua obrigatória

  // Solicitação (bug/melhoria/sugestão) — categoria='solicitacao'
  categoria?: TarefaCategoria;
  departamento_solicitacao?: SolicitacaoDepartamento | null;
  tipo_solicitacao?: SolicitacaoTipo | null;
  // Texto livre preenchido quando tipo_solicitacao é 'outro'/'outro_tecnologia' — o CHECK fechado
  // não classifica, então a pessoa escreve do que se trata (ver solicitacaoTipos.ts isTipoOutro).
  tipo_outro_detalhe?: string | null;
  solicitacao_status?: SolicitacaoStatus | null;
  data_inicio?: string | null; // carimbada automaticamente quando alguém pega a demanda (Direcionar) — início da barra no Gantt

  // Dependência: esta solicitação está bloqueada por uma PESSOA e um MOTIVO — não por outra
  // solicitação (ver TarefasView.tsx handleSolicitacaoBloqueadaPorPessoaChange).
  bloqueada_por_pessoa_id?: string | null;
  bloqueada_por_texto?: string | null; // motivo do bloqueio, ex.: "aguardando aprovação do financeiro"

  // Dificuldade informada ao concluir a solicitação (#6354): fácil, média ou difícil.
  dificuldade?: 'facil' | 'media' | 'dificil' | null;

  // Subtarefas — item filho de uma Solicitação "mãe", com dono/status/cronômetro próprios
  parent_id?: number | null;
  subtarefas?: Tarefa[]; // preenchido no client (filtro local sobre a lista já carregada), não vem do banco

  // Projeto ao qual a tarefa pertence (módulo Projetos). Null = tarefa avulsa, que é o caso da
  // maioria. Só a UI de Solicitação oferece o seletor. Subtarefa não carrega projeto_id: ela
  // pertence ao projeto pela mãe.
  projeto_id?: number | null;

  created_at: string;
  negociacao_id?: number | null; // Added context
  negociacao?: {
    id: number;
    titulo: string;
    consumo_medio_kwh?: number;
    status?: string;
    stage?: string;
    cidade?: string | null;
    bairro?: string | null;
    segmento?: string | null;
    whatsapp?: string | null;
  };
}

export interface ComentarioTarefa {
  id: number;
  tarefa_id: number;
  user_id: string;
  conteudo: string;
  // Imagens anexadas ao próprio comentário (print colado ou arquivo escolhido).
  // Bucket público 'tarefas-anexos', prefixo comentarios/.
  anexos_url?: string[] | null;
  created_at: string;
  // Apagado pelo autor (#6287): o registro fica, a tela mostra "Comentário apagado".
  apagado_em?: string | null;
  apagado_por?: string | null;
  user?: Profile; // Joined
}

export type ProjetoEtapa =
  | 'planejamento'
  | 'em_execucao'
  | 'em_teste'
  | 'concluido'
  | 'pausado'
  | 'cancelado';

/**
 * Iniciativa que agrupa solicitações da Central de Solicitações.
 *
 * Não tem coluna de progresso: a porcentagem é derivada das tarefas vinculadas em
 * src/lib/projetos/progresso.ts. `etapa` é a fase declarada por quem conduz — outra informação,
 * exibida ao lado da porcentagem.
 */
export interface Projeto {
  id: number;
  nome: string;
  descricao?: string | null;
  etapa: ProjetoEtapa;
  tags: string[];
  responsavel_id?: string | null;
  prazo?: string | null;
  criado_por?: string | null;
  created_at: string;
  updated_at: string;
  responsavel?: Pick<Profile, 'id' | 'full_name' | 'avatar_url'> | null;
}

/**
 * Adesão ao boleto único, por UC (migration 20260912180000).
 *
 * É o histórico, não o estado: revogar não apaga a linha, cria `revogado_em`, e reaceite
 * é linha nova. `unidades_consumidoras.boleto_unico_aceite_em/_canal` são espelhos
 * mantidos por trigger — leia de lá, escreva aqui.
 */
export type BoletoUnicoAceiteCanal = 'portal' | 'whatsapp' | 'manual' | 'termo';

export interface BoletoUnicoAceite {
  id: number;
  uc_id: number;
  canal: BoletoUnicoAceiteCanal;
  aceito_em: string;
  /** A cláusula vale de OUT/2026 em diante; aceite em setembro não libera antes disso. */
  vigencia_a_partir_de: string;
  revogado_em?: string | null;
  revogado_motivo?: string | null;
  /**
   * Prova de que o débito automático da Equatorial saiu (decisão #5571, Opção 2). O
   * ATESTADO é o analista confirmando com o cliente — destrava na hora; a CONFIRMAÇÃO é o
   * robô carimbando quando a fatura seguinte chega limpa.
   */
  cancelamento_atestado_em?: string | null;
  cancelamento_atestado_por?: string | null;
  cancelamento_confirmado_em?: string | null;
  registrado_por?: string | null;
  revogado_por?: string | null;
  /**
   * Prova do aceite pelo Portal (#5575, migration 20260914150000), preenchida pelo servidor na
   * função `aceitar_boleto_unico_portal`: a versão da cláusula aceita (`avisos_consumidor.id`),
   * o IP e o navegador. NULL em aceite manual.
   */
  aviso_id?: string | null;
  ip?: string | null;
  user_agent?: string | null;
  created_at: string;
}

/**
 * Disparo do aviso de adesão ao boleto único, por UC (migration 20260913120000).
 *
 * Quem escreve é o fluxo n8n da #5578; quem lê é o painel da #5579. Guarda também a
 * tentativa que falhou (`ok = false`), senão uma rodada que não entregou nada fica
 * indistinguível de uma rodada que nunca aconteceu. Os espelhos
 * `unidades_consumidoras.boleto_unico_disparo_em/_canal` só refletem `ok = true`.
 */
/** 'nenhum' = a UC não tinha WhatsApp nem e-mail autorizado; sempre com `ok = false`. */
export type BoletoUnicoDisparoCanal = 'whatsapp' | 'email' | 'manual' | 'nenhum';

export interface BoletoUnicoDisparo {
  id: number;
  uc_id: number;
  canal: BoletoUnicoDisparoCanal;
  /** Nome do template disparado — o conteúdo vive na UaisyChat/Meta, não aqui. */
  template?: string | null;
  enviado_em: string;
  ok: boolean;
  erro?: string | null;
  registrado_por?: string | null;
  /** Item da fila que produziu este envio (migration 20260914130000). NULL = registro manual. */
  fila_id?: number | null;
  created_at: string;
}

/**
 * Fila do aviso de adesão (#5578, migration 20260914130000). Escrita só pelas funções
 * `enfileirar_aviso_boleto_unico` e `cancelar_avisos_pendentes_boleto_unico`; o resultado de
 * cada envio vai para `BoletoUnicoDisparo`.
 */
export type BoletoUnicoDisparoFilaStatus = 'pendente' | 'enviando' | 'enviado' | 'falhou' | 'cancelado';

export interface BoletoUnicoDisparoFila {
  id: number;
  uc_id: number;
  /** A onda: parar e auditar por ela. */
  lote_id: string;
  status: BoletoUnicoDisparoFilaStatus;
  scheduled_at: string;
  /** Decidido na hora de mandar, não ao enfileirar. */
  canal?: 'whatsapp' | 'email' | 'nenhum' | null;
  tentativas: number;
  iniciado_em?: string | null;
  processado_em?: string | null;
  erro?: string | null;
  solicitado_por?: string | null;
  cancelado_por?: string | null;
  created_at: string;
}

export interface ComentarioProjeto {
  id: number;
  projeto_id: number;
  user_id: string;
  conteudo: string;
  anexos_url?: string[];
  created_at: string;
  user?: Pick<Profile, 'id' | 'full_name' | 'avatar_url'> | null;
}

/**
 * Documentação que sobra de um projeto, para quem entra depois — o que antes ficava em
 * arquivo .md solto na raiz do repositório. Ver migration 20260912120000.
 *
 * `documento` é markdown escrito no VigorHub e renderizado na tela (é o que se lê);
 * `anexo` é arquivo no bucket público `tarefas-anexos` (planilha, PDF, imagem de fluxo).
 * O CHECK do banco garante `conteudo` no primeiro e `arquivo_url` no segundo.
 */
export type ProjetoDocumentoTipo = 'documento' | 'anexo';

export interface ProjetoDocumento {
  id: number;
  projeto_id: number;
  tipo: ProjetoDocumentoTipo;
  titulo: string;
  /** Markdown; preenchido quando tipo='documento'. */
  conteudo?: string | null;
  /** URL pública; preenchida quando tipo='anexo'. */
  arquivo_url?: string | null;
  arquivo_nome?: string | null;
  arquivo_tamanho?: number | null;
  /** Ordem de leitura definida por quem documenta; empate cai para created_at. */
  ordem: number;
  criado_por?: string | null;
  atualizado_por?: string | null;
  created_at: string;
  updated_at: string;
  autor?: Pick<Profile, 'id' | 'full_name' | 'avatar_url'> | null;
}

// Sessão de cronômetro (Iniciar/Pausar/Finalizar) de uma Solicitação — ou um
// registro manual de tempo estimado (is_manual=true), quando o responsável
// não rodou o cronômetro mas quer contabilizar o tempo gasto mesmo assim.
export interface TarefaTimeEntry {
  id: number;
  tarefa_id: number;
  user_id: string;
  started_at: string;
  ended_at?: string | null; // null enquanto o cronômetro está rodando
  duration_seconds?: number | null;
  is_manual?: boolean;
  // Encerramento automático do cronômetro esquecido (cron encerrar_cronometros_esquecidos).
  // duration_bruta_seconds guarda o que teria sido carimbado sem o corte — serve pra medir
  // o quanto a regra está cortando sem depender de alguém reclamar.
  encerrado_automaticamente?: boolean;
  motivo_encerramento?: 'heartbeat' | 'ultimo_sinal' | 'piso_18h' | 'teto_12h' | 'virada_do_dia' | 'sem_sinal' | null;
  // Última batida do client enquanto o cronômetro rodava — sinal de que a máquina ainda
  // estava de pé. É o que permite cortar no minuto real em vez do piso das 18h.
  last_heartbeat_at?: string | null;
  duration_bruta_seconds?: number | null;
  created_at: string;
  user?: Profile; // Joined
}

// Trilha de auditoria de mudança de solicitacao_status — alimenta o dashboard de Auditoria
export interface TarefaStatusEvent {
  id: number;
  tarefa_id: number;
  user_id?: string | null;
  from_status?: string | null;
  to_status: string;
  created_at: string;
  user?: Profile; // Joined
}

export interface DadosTecnicosEquatorial {
  titularidade?: string;
  nParceiro?: string;
  grupo?: string;
  tipoFornecimento?: string;
  novoNumeroUc?: string;
  dataLeitura?: string;
  dataApresentacao?: string;
  dataVencimento?: string;
  dataProcessamento?: string;
  checks?: {
    atualizada?: boolean;
    grupoB?: boolean;
    explicacaoGrupoB?: string;
    beneficio?: boolean;
    consumo500?: boolean;
    explicacaoConsumo500?: string;
    possuiGD?: boolean;
    explicacaoGD?: string;
    injetando?: boolean;
    mesmoTitular?: boolean;
    distratoRecebido?: boolean;
  };
  historyTable?: {
    month: string;
    consumo: number;
    injecao?: number;
    vencimento: string;
    pagamento: string;
    emAberto?: boolean;
    delayDays: number;
    ocorrencia: string;
  }[];
  faturaUrl?: string | null;
  faturaNome?: string | null;
  faturaType?: string | null;
}

/** Contas Sicoob da Vigor (tabela `contas_sicoob`). Rótulos e regras em src/lib/contaSicoob.ts. */
export type ContaSicoob = 'associacao' | 'gestao' | 'hsolar';

export interface UnidadeConsumidora {
  id: number;
  numero_uc: string;
  nome_cliente: string;
  endereco: string;
  situacao: 'Ativa' | 'Ativa-S/-Rateio' | '1° Ciclo' | '2° Ciclo' | 'Cancelada' | 'Reativacao' | 'Pausada' | null;
  /** Quando virou "Cancelada" — carimbada pelo banco (migration 20260928150000, #6005). */
  cancelada_em?: string | null;
  /** 'registrada' ou de onde a data foi estimada (ver src/lib/dataCancelamentoUc.ts). */
  cancelada_em_origem?: string | null;
  status_financeiro: 'Adimplente' | 'Inadimplente' | 'Não se aplica' | null;
  created_at: string;
  // New Fields
  negociacao_id?: number;
  num_parceiro?: string;
  data_leitura?: string;
  data_apresentacao?: string;
  data_vencimento?: string;
  data_processamento?: string;
  hist_manual_mes_referencia?: string | null;
  hist_manual_leitura_anterior?: string | null;
  hist_manual_leitura_atual?: string | null;
  hist_manual_proxima_leitura?: string | null;
  hist_manual_data_apresentacao?: string | null;
  hist_manual_data_vencimento?: string | null;
  cep?: string;
  logradouro?: string;
  numero?: string;
  complemento?: string;
  quadra?: string;
  lote?: string;
  bairro?: string;
  cidade?: string;
  estado?: string;
  /**
   * Decisão COMERCIAL da análise cadastral, congelada. Enviesada ~16% para cima
   * de propósito (`ANC-R30`) para dimensionar o contrato com folga. Alimenta
   * faixa de público, comissão, aditivo e o corte de 700 kWh do pós-venda.
   * NÃO é recalculada sozinha — ver `consumo_observado_kwh`.
   */
  consumo_medio_kwh?: number;
  /**
   * Média SIMPLES das faturas reais dos últimos 12 meses, sem viés. Gravada pela
   * rotina diária `recalcular-consumo-observado`. É o número que aloca energia
   * no rateio e mede cobertura de saldo. `null` = ainda sem base.
   */
  consumo_observado_kwh?: number | null;
  /** Quantas faturas entraram na média. Abaixo de 3 o valor não deve alocar energia nem travar a UC. */
  consumo_observado_meses?: number | null;
  /**
   * Coeficiente de variação dos meses observados. Acima de 0,6 a UC é marcada
   * como instável: o número segue visível e utilizável, mas o travamento
   * automático não age sobre ela. Sazonalidade real fica em 0,15–0,25.
   */
  consumo_observado_dispersao?: number | null;
  /** De onde veio `consumo_medio_kwh`: 'vendedor' | 'analise' | 'manual' | 'observado'. */
  consumo_medio_origem?: string | null;
  consumo_medio_definido_por?: string | null;
  consumo_medio_definido_em?: string | null;
  /** Motivo da última alteração manual — exigido quando já há comissão calculada. */
  consumo_medio_justificativa?: string | null;
  /** Quando a rotina gravou. Acima de 45 dias o valor é tratado como desatualizado. */
  consumo_observado_em?: string | null;
  tipo_ligacao?: 'Monofásico' | 'Bifásico' | 'Trifásico';
  grupo_tarifario?: 'A1' | 'A2' | 'A3' | 'A3a' | 'A4' | 'AS' | 'B1' | 'B2' | 'B3' | 'B4';
  concessionaria?: string;
  desconto_tarifa?: number;
  desconto_bandeira?: number;
  /** Vencimento do boleto da Vigor: nulo = igual ao da Equatorial. Ver src/lib/regraVencimentoUc.ts. */
  vencimento_regra?: 'dia_fixo' | 'dias_apos_equatorial' | 'mes_seguinte' | null;
  /** Dia do mês (dia_fixo) ou dias depois da Equatorial (dias_apos_equatorial). */
  vencimento_dia?: number | null;
  /** Quem vendeu — só na UC avulsa (sem negociação): com negociação vale negociacoes.plano_modalidade. */
  plano_modalidade?: 'vendedor' | 'vendedor_interno' | 'parceiro' | 'empresa_parceira' | null;
  /** Só na UC avulsa: com negociação vale negociacoes.fidelidade. */
  fidelidade?: string | null;
  carencia?: string;
  valor_tarifa?: number;
  titular_fatura?: string;
  cpf_cnpj_titular?: string;
  data_nascimento_titular?: string;
  cpf_cnpj_cliente?: string;
  data_nascimento_cliente?: string;
  /** Quem paga o boleto (tabela `pagadores`), separado do cliente. Ver src/lib/pagadorUc.ts. */
  pagador_id?: string | null;
  /** Coluna de passagem: vai no update com o motivo da mudança e o banco sempre devolve vazia (ver src/lib/alteracoesCadastro.ts). */
  motivo_alteracao?: string | null;
  whatsapp_cliente?: string | null;
  /** Segundo número que recebe a cobrança, quando o cliente pede mais de um destino. */
  whatsapp_cliente_2?: string | null;
  email_cliente?: string | null;
  outro_email?: string | null;
  observacoes_uc?: string;
  outro_numero_uc?: string;
  num_medidor?: string;
  // Fluxo de Rateio legado (Rateio Antigo) — gravados por sync_uc_from_rateio a cada upload de planilha.
  unidade_geradora?: string | null;
  percent_rateio?: number | null;
  // Módulo de Rateio (novo) — UG à qual esta UC está vinculada para simulação de excedente.
  rateio_unidade_geradora_id?: string | null;
  // Módulo de Rateio — auditoria de travamento/destravamento (Fatia 6).
  rateio_travado_em?: string | null;
  rateio_motivo_travamento?: 'excesso_saldo' | 'manual' | null;
  rateio_destravado_por?: string | null;
  rateio_destravado_em?: string | null;
  // Módulo de Rateio (Fatia 8) — GD I/II é só cadastro/exibição, não alimenta
  // faturamento mínimo. rateio_vinculado_em usado na previsão de leitura.
  modalidade_gd?: 'GD_I' | 'GD_II' | null;
  rateio_vinculado_em?: string | null;
  // Gate de vínculo com UG (Seção 2 da spec) — UCs pré-existentes nascem true.
  cadastro_aprovado?: boolean;
  // UC que absorve o percentual excedente do rateio de uma UG (ex: "Banco de
  // Créditos Vigor Energy") — usada na tela Gestão da Unidade Âncora.
  rateio_e_banco_creditos?: boolean;
  dados_tecnicos_equatorial?: DadosTecnicosEquatorial | null;
  url_termo?: {
    url_pdf: string;
    data_criacao: string;
    tipo_cliente?: string;
    numero_termo?: string;
    /** Marcado pelo admin como o termo efetivamente assinado/válido (override do duplo envio). */
    validado?: boolean;
    /** Quando o cliente assinou este termo no Autentique (ISO). */
    data_assinatura?: string;
    /** false = assinou, mas a biometria (SERPRO) ainda não voltou. */
    assinatura_verificada?: boolean;
  }[] | null;
  url_simulacao?: {
    url_pdf: string;
    data_criacao: string;
    valor_mensal?: string;
    valor_economia?: string;
    numero_simulacao?: string;
  }[] | null;
  documentos_json?: any | null;
  /** Conta Sicoob em que os boletos da UC saem. Vale para a próxima emissão. */
  conta_sicoob?: ContaSicoob | null;
  /** Legado de quando eram duas contas (true = Gestão). O banco mantém sincronizado
   *  com `conta_sicoob`; use a coluna nova. */
  emitir_em_vigor_gestao?: boolean | null;
  receber_cobranca_email?: boolean | null;
  /**
   * Exceção ao bloqueio de envio por espelho (migration 20260914120000). Ligada, o
   * espelho da distribuidora pode ser enviado a este cliente mesmo sem PIX nem código
   * de barras — ver bloqueiaEnvioPorEspelho em src/lib/faturaUtils.ts. O banco exige
   * motivo quando a flag está ligada.
   */
  permite_envio_espelho?: boolean | null;
  permite_envio_espelho_motivo?: string | null;
  permite_envio_espelho_em?: string | null;
  permite_envio_espelho_por_nome?: string | null;
  // Adesão à modalidade de boleto único (Vigor + Equatorial combinados) — ver
  // trg_enfileirar_pagamento_equatorial e pagamento_equatorial_fila.
  boleto_unico?: boolean | null;
  /**
   * Espelhos de `boleto_unico_aceites`, mantidos por trigger (migration 20260912180000).
   * `boleto_unico_aceite_em` NULL = cliente ainda não aderiu. Não escrever direto: insira
   * na tabela de aceites, que é a fonte de verdade e guarda revogação/histórico.
   * `boleto_unico` (liberação) e o aceite são coisas distintas — ver
   * src/lib/boletoUnico/aceite.ts.
   */
  boleto_unico_aceite_em?: string | null;
  boleto_unico_aceite_canal?: string | null;
  /**
   * Modelo de cobrança escolhido no CRM ao gerar o termo (#6367). Não liga o boleto único:
   * com `modelo_cobranca_aceite_assinatura`, a assinatura do termo vira aceite de canal 'termo'
   * (migration 20261021150000). Ver src/lib/boletoUnico/modeloCobranca.ts.
   */
  modelo_cobranca?: 'unificado' | 'separado' | null;
  modelo_cobranca_aceite_assinatura?: boolean | null;
  modelo_cobranca_em?: string | null;
  modelo_cobranca_por?: string | null;
  /**
   * Espelhos de `boleto_unico_disparos`, mantidos por trigger (migration 20260913120000) —
   * último disparo BEM-SUCEDIDO do aviso de adesão. NULL = ninguém avisou este cliente
   * ainda. Mesma regra dos espelhos de aceite: leia daqui, escreva na tabela.
   */
  boleto_unico_disparo_em?: string | null;
  boleto_unico_disparo_canal?: string | null;
  // Quem paga a conta da distribuidora quando o cliente paga o boleto único:
  // true (padrão) = o VigorHub, criando um bill no Asaas; false = a empresa parceira paga
  // por fora e a baixa é manual. Não confundir com faturas.debito_automatico_equatorial,
  // que é a distribuidora debitando na conta do próprio cliente.
  repasse_equatorial_automatico?: boolean | null;
  segmento?: string | null;
  // Empresa Parceira (NULL = UC da Vigor Energy)
  empresa_parceira_id?: string | null;
  // Cliente próprio da Gran, fora da parceria com a Vigor (migration 20260925143833):
  // a Vigor não calcula nem emite para a UC; a cobrança sai pelo boleto avulso da Gran.
  // O banco não aceita junto com empresa_parceira_id.
  fora_parceria_vigor?: boolean | null;
  // UC de empresa parceira: % da tarifa cheia que é da Vigor (65 = deságio de 35%).
  // A parceira fica com o residual. Só Financeiro ou adm alteram (gatilho no banco).
  percentual_vigor?: number | null;
  percentual_vigor_alterado_por?: string | null;
  percentual_vigor_alterado_em?: string | null;
  // ID do customer no Asaas (cacheado para evitar criação duplicada) e de qual
  // conta ele é — cliente de uma conta Asaas não existe na outra.
  asaas_customer_id?: string | null;
  asaas_customer_conta?: 'vigor' | 'parceira' | null;
}

export interface Notification {
  id: string;
  user_id: string;
  type: 'task_assigned' | 'task_completed' | 'negotiation_assigned' | 'mention' | 'message' | 'analysis_completed' | 'password_reset_request' | 'system' | 'parceiro_cadastro' | 'pendencia_documental' | 'contract_signed' | 'contract_pending' | 'termo_reprovado' | 'consumo_divergencia' | 'cobranca_d15' | 'repasse_equatorial_falhou' | 'sicoob_pagamento_sem_fatura' |'rateio_espelho_pendente'| 'mora_prazo_5du' | 'mora_sem_assinatura' | 'documento_assinado' | 'documento_sem_envio' | 'advertencia_prazo' | 'cobranca_followup' | 'lembrete_assinatura'  | 'troca_titularidade_assinada' | 'termo_email_nao_entregue' | 'cobranca_acao_manual' | 'solicitacao_prazo_proximo' | 'solicitacao_ausencia' | 'geradora_alarme' | 'geradora_alerta' | 'ia_alerta' | 'fatura_status_sync_pendente' | 'fatura_status_sync_decidido' | 'troca_dono_uc' | 'notificacao_encaminhada';
  title: string;
  content: string;
  link?: string;
  read: boolean;
  created_at: string;
  metadata?: Record<string, any> | null;
}

export interface NotificationPreferences {
  user_id: string;
  email_enabled: boolean;
  push_enabled: boolean;
  task_assigned: boolean;
  task_completed: boolean;
  task_mention: boolean;
  negotiation_assigned: boolean;
  negotiation_mention: boolean;
  // Contratos (apenas vendedor / vendedor_parceiro)
  contract_signed: boolean;
  contract_pending_48h: boolean;
  cobranca_followup: boolean;
  solicitacao_prazo_proximo: boolean;
}

export type CobrancaStatusNegociacao =
  | 'aguardando_contato'
  | 'negociando'
  | 'negociado'
  | 'pago'
  // Encaminhado para a notificação extrajudicial pelo "Gerenciar Cobrança":
  // sai do contato e vai para quem notifica (07/10/2026).
  | 'notificacao_extrajudicial'
  | 'notificacao_realizada'
  | 'sem_pagamento'
  | 'negativacao_pendente'
  | 'negativacao_realizada';

// Acompanhamento de cobrança por cliente (CPF/CNPJ), não por fatura — um
// cliente pode ter várias faturas em "Cobrança enviada" ao mesmo tempo.
export interface CobrancaCliente {
  id: string;
  cpf_cnpj_cliente: string;
  cliente_nome?: string | null;
  responsavel_cobranca_id?: string | null;
  status_negociacao: CobrancaStatusNegociacao;
  valor_negociado?: number | null;
  data_pagamento_negociado?: string | null;
  proximo_contato_em?: string | null;
  negativado_em?: string | null;
  negativado_por_id?: string | null;
  negativado_por_nome?: string | null;
  negativacao_realizada_em?: string | null;
  negativacao_realizada_por_id?: string | null;
  negativacao_realizada_por_nome?: string | null;
  // Retirada da negativação quando o cliente regulariza (#6147). Enquanto
  // negativacao_realizada_em estiver preenchido e este não, o nome continua sujo.
  negativacao_retirada_em?: string | null;
  negativacao_retirada_por_id?: string | null;
  negativacao_retirada_por_nome?: string | null;
  // Encaminhamento para a notificação extrajudicial: quem mandou e quando. O
  // painel mostra o cliente para quem notifica até a notificação ser gerada.
  notificacao_encaminhada_em?: string | null;
  notificacao_encaminhada_por_id?: string | null;
  notificacao_encaminhada_por_nome?: string | null;
  // Recebimento confirmado (status 'pago'): quem fechou a cobrança e quanto
  // entrou de fato — valor_pago pode divergir de valor_negociado.
  pago_em?: string | null;
  pago_por_id?: string | null;
  pago_por_nome?: string | null;
  valor_pago?: number | null;
  created_at: string;
  updated_at: string;
}

/**
 * Por onde uma tentativa de contato de cobrança foi feita. Mesma lista da fila
 * de contato humano (`CobrancaAcaoCanal`) mais 'carta' — no passivo antigo a
 * carta com AR é passo comum antes da negativação, e a fila da régua não a tem.
 * Definido por extensão de propósito: duas listas soltas divergiriam.
 */
export type CobrancaCanalContato = CobrancaAcaoCanal | 'carta';

/** Desfecho de uma tentativa de contato de cobrança. */
export type CobrancaResultadoContato =
  | 'atendeu' | 'nao_atendeu' | 'sem_retorno' | 'prometeu_pagar'
  | 'recusou_negociar' | 'contato_invalido' | 'outro';

export interface CobrancaNegociacaoHistorico {
  id: string;
  cobranca_cliente_id: string;
  user_id?: string | null;
  user_nome?: string | null;
  status_anterior?: string | null;
  status_novo?: string | null;
  observacao?: string | null;
  created_at: string;
  /** 'status' | 'observacao' | 'contato' — ver migration 20260825120000. */
  tipo_evento?: 'status' | 'observacao' | 'contato' | null;
  canal?: CobrancaCanalContato | null;
  resultado?: CobrancaResultadoContato | null;
  /** Quando o contato aconteceu, que pode ser anterior a `created_at`. */
  contato_em?: string | null;
}

/** Origem de um evento na timeline consolidada de cobrança. */
export type CobrancaFonteEvento =
  | 'negociacao'   // mudança de status ou observação livre
  | 'contato'      // tentativa de contato registrada à mão
  | 'lembrete'     // régua automática (D-1 / D+3 / D+7)
  | 'acao_manual'  // fila de contato humano (D+10 / D+17 / D+24)
  | 'notificacao'  // notificação extrajudicial de mora
  | 'comunicado';  // comunicado de advertência

/**
 * Uma linha do histórico de cobrança consolidado, como devolvida pela função
 * `timeline_cobranca_cliente` (migration 20260825130000). O histórico vive em
 * cinco tabelas com chaves diferentes; o banco é quem junta.
 */
export interface EventoTimelineCobranca {
  fonte: CobrancaFonteEvento;
  em: string | null;
  titulo: string;
  detalhe?: string | null;
  autor?: string | null;
  url?: string | null;
}

export interface ConferenciaDocs {
  rg_cnh?: { presenca?: 'Completo' | 'Incompleto'; legivel?: boolean; ativo?: boolean };
  contrato_social?: { presenca?: 'Completo' | 'Incompleto'; legivel?: boolean; cnpj_ativo?: boolean; nome_socio_administrador?: string; cpf_socio_administrador?: string };
  cartao_cnpj?: { apresentado?: boolean; status?: 'Ativa' | 'Inativa' };
  ata_eleicao?: { presenca?: 'N/A' | 'Completo' | 'Incomp.'; legivel?: boolean; atual?: boolean; titular_divergente?: boolean; nome_representante?: string; cpf_representante?: string; validade_ata?: string };
  ata_constituicao?: { presenca?: 'N/A' | 'Completo' | 'Incomp.'; legivel?: boolean; data_constituicao?: string; cnpj_condominio_str?: string };
  fatura_energia?: { apresentado?: boolean; status?: 'Cadastro Atualizado' | 'Cadastro Desatualizado'; titular_divergente?: boolean; legivel?: boolean };
  procuracao?: { presenca?: 'N/A' | 'Completo' | 'Incomp.'; legivel?: boolean; atual?: boolean; titular_divergente?: boolean; nome_procurador?: string; cpf_procurador?: string; validade_procuracao?: string; poderes_especificos?: string };
  contrato_locacao?: { presenca?: 'N/A' | 'Completo' | 'Incomp.'; legivel?: boolean; atual?: boolean; titular_divergente?: boolean; data_inicio?: string; data_fim?: string; locatario_nome?: string };
  contrato_compra_venda?: { presenca?: 'N/A' | 'Completo' | 'Incomp.'; legivel?: boolean; atual?: boolean; titular_divergente?: boolean; data_escritura?: string; comprador_nome?: string };
  convencao_condominio?: { presenca?: 'Completo' | 'Incompleto'; legivel?: boolean };
  cnpj_condominio?: { apresentado?: boolean; status?: 'Ativa' | 'Inativa' };
  possui_gd?: boolean;
  termo_adesao?: {
    endereco_correto?: boolean;
    endereco_rua?: boolean;
    endereco_quadra_lote?: boolean | 'nao_existe';
    endereco_numero?: boolean | 'nao_existe';
    endereco_bairro?: boolean;
    endereco_cidade?: boolean;
    endereco_cep?: boolean;
    assinante_titular_poderes?: boolean;
    dados_associado_corretos?: boolean;
    desconto_fidelidade_corretos?: boolean;
    ucs_conferem_faturas?: boolean;
    todas_ucs_titular?: boolean;
    termo_assinado?: boolean;
    autenticidade_assinaturas?: boolean;
    desconto_concedido_certo?: boolean;
    quantidade_unidades_certa?: boolean;
    dados_titular?: boolean;
    contato_correto?: boolean;
    dados_ucs_corretos?: boolean;
    assinante_tem_poderes?: boolean;
  };
}

export interface SerasaExperianData {
  // ── Score & Identificação (PF + PJ) ──
  score?: number;
  score_mensagem?: string;
  risco_credito?: string;           // Classificação derivada do defaultRate da API: Baixo | Médio | Alto | Crítico
  chance_pagamento?: number | string | null;
  situacao_receita_federal?: string;
  data_situacao_rf?: string; // PF: statusDate — data de atualização na Receita Federal
  nome_mae?: string; // PF only
  cidade_pf?: string; // PF: cidade do cadastro base
  uf_pf?: string; // PF: UF do cadastro base

  // ── Anotações Negativas (PF + PJ) ──
  anotacoes_negativas?: number;
  valor_anotacoes_negativas?: number;
  dividas_comerciais?: number;       // PEFIN
  valor_dividas_comerciais?: number;
  dividas_bancarias?: number;        // REFIN
  valor_dividas_bancarias?: number;
  dividas_vencidas?: number;         // Convem (PF) / Vencidas (PJ)
  valor_dividas_vencidas?: number;
  quantidade_protestos?: number;
  valor_total_protestos?: number;
  quantidade_cheques_sf?: number;
  valor_total_cheques_sf?: number;

  // ── PF: Participações Societárias ──
  participacoes_societarias?: {
    cnpj: string;
    razao_social: string;
    capital_percentual: string;
    desde?: string;         // participationInitialDate
    uf: string;
    situacao_rf: string;
    data_situacao?: string;   // companyStatusDate
    atualizado_em?: string;   // updateDate
  }[];

  // ── PF: Documentos Roubados ──
  documentos_roubados?: boolean;
  data_nascimento?: string;

  // ── PJ: Identificação Cadastral ──
  razao_social?: string;
  nome_fantasia?: string;
  data_fundacao?: string;
  cnae?: string;
  atividade_economica?: string;
  natureza_juridica?: string;
  endereco_sede?: string;
  municipio_uf?: string;

  // ── PJ: Quadro Societário ──
  quadro_societario?: {
    socios: { nome: string; cpf_cnpj: string; capital_percentual: string; anotacoes: boolean }[];
    administradores: { nome: string; cargo: string; cpf_cnpj: string; anotacoes: boolean }[];
  };

  // ── PJ: Probabilidade de Inadimplência ──
  probabilidade_inadimplencia?: number;

  // ── Consultas à Serasa (PF + PJ) ──
  consultas_serasa?: {
    mes_atual: number;
    mes_anterior: number;
    detalhes: { data: string; quantidade: number; segmento_ou_nome: string; cnpj_consultante?: string }[];
  };

  // ── Campos legados (mantidos para compatibilidade com motor de risco) ──
  score_faixa?: string;
  quantidade_pendencias?: number;
  valor_total_pendencias?: number;
  participacao_empresas?: string[];
  quantidade_consultas_recentes?: number;
  ultima_consulta?: string;
  ccf?: boolean;
  refin?: boolean;
  pefin?: boolean;
  acoes_judiciais?: number;
  valor_acoes_judiciais?: number;
  participacao_falencias?: string;
  observacoes?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Lemit (lemitti.com) — enriquecimento de dados de prospecção via CPF/CNPJ.
// Estrutura espelha a doc da API (contexto/documentacao/API - lemit.mhtml).
// ─────────────────────────────────────────────────────────────────────────────
export interface LemitTelefone {
  ddd?: number;
  numero?: string;
  plus?: boolean | null;
  ranking?: number;
  whatsapp?: boolean;
}

export interface LemitEmail {
  email?: string;
  ranking?: number;
  possui_cookie?: boolean;
}

export interface LemitEndereco {
  endereco?: string;
  tipo_logradouro?: string;
  logradouro?: string;
  numero?: string;
  complemento?: string;
  bairro?: string;
  cidade?: string;
  uf?: string;
  cep?: string;
  tipo?: string;
  ranking?: number;
}

export interface LemitCarro {
  placa?: string;
  marca?: string;
  ano_fabricacao?: number;
  ano_modelo?: number;
  renavan?: string;
  chassi?: string;
  data_licenciamento?: string;
  ranking?: number;
}

export interface LemitVinculo {
  cpf_vinculo?: string;
  nome_vinculo?: string;
  tipo_vinculo?: string;
}

export interface LemitParticipacao {
  nome?: string;
  cnpj?: string;
  capital_social?: number;
  participacao_socio?: number;
  data_fundacao?: string;
  situacao_cadastral?: string;
}

export interface LemitSocio {
  cpf?: string;
  nome?: string;
  participacao?: number;
  capital_social?: number;
}

export interface LemitCnae {
  numero?: string;
  tipo?: string;
  segmento?: string;
  descricao?: string;
}

export interface LemitRiscoCredito {
  cpf_cnpj?: string;
  score_credito?: string;
}

export interface LemitPessoaData {
  cpf?: string;
  nome?: string;
  data_nascimento?: string;
  sexo?: string;
  nome_mae?: string;
  falecido?: boolean;
  situacao_cpf?: string;
  renda?: number;
  ocupacao?: string;
  celulares?: LemitTelefone[];
  fixos?: LemitTelefone[];
  emails?: LemitEmail[];
  enderecos?: LemitEndereco[];
  carros?: LemitCarro[];
  vinculos?: LemitVinculo[];
  risco_credito?: LemitRiscoCredito | null;
  participacao_societaria?: LemitParticipacao[];
  _data_consulta?: string;
}

export interface LemitEmpresaData {
  cnpj?: string;
  razao_social?: string;
  data_fundacao?: string;
  nome_fantasia?: string;
  tipo?: string;
  situacao?: string;
  cnae?: LemitCnae | null;
  endereco?: LemitEndereco | null;
  celulares?: LemitTelefone[];
  fixos?: LemitTelefone[];
  emails?: LemitEmail[];
  socios?: LemitSocio[];
  carros?: LemitCarro[];
  _data_consulta?: string;
}

export type LemitTipo = 'pessoa' | 'empresa';

export interface LemitConsulta {
  id: number;
  documento: string;
  tipo: LemitTipo;
  resultado: LemitPessoaData | LemitEmpresaData;
  resultado_raw?: any;
  segmento?: string | null;
  consultado_por?: string | null;
  consultado_por_nome?: string | null;
  negociacao_id?: number | null;
  custo_estimado?: number | null;
  source?: 'api' | 'cache' | null;
  created_at: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Fila da Prospecção (Lista Fria) — os lotes de geocode/Lemit rodam no servidor
// (edge function processar-fila-prospeccao acionada por pg_cron), não mais num
// laço dentro do navegador. Ver src/services/prospeccaoFila.ts.
// ─────────────────────────────────────────────────────────────────────────────

export type ProspeccaoJobTipo = 'geocode' | 'lemit_documento' | 'lemit_dono';

export type ProspeccaoJobStatus =
  | 'pendente'         // enfileirado, aguardando o worker (ou a janela abrir)
  | 'rodando'
  | 'pausado_janela'   // fora do horário noturno configurado
  | 'pausado_cota'     // teto diário da Lemit atingido; retoma amanhã
  | 'concluido'
  | 'cancelado'
  | 'erro';            // disjuntor: erros seguidos de infraestrutura

export interface ProspeccaoJob {
  id: string;
  tipo: ProspeccaoJobTipo;
  status: ProspeccaoJobStatus;
  criado_por: string | null;
  criado_por_nome: string | null;
  /** Horário (BRT, "22:00:00") em que o job pode rodar. null = agora, a qualquer hora. */
  janela_inicio: string | null;
  janela_fim: string | null;
  /** Teto de itens por dia DESTE lote. null = vai até a cota da conta Lemit acabar. */
  max_por_dia: number | null;
  /** Derivado de `janela_inicio != null`; mantido por compatibilidade. */
  usa_janela: boolean;
  usa_cota_lemit: boolean;
  total: number;
  processados: number;
  sucesso: number;
  falhas: number;
  ultimo_erro: string | null;
  heartbeat_em: string | null;
  iniciado_em: string | null;
  concluido_em: string | null;
  created_at: string;
  updated_at: string;
}

export interface LemitCota {
  usados: number;
  limite: number;
  restante: number;
}

export interface AnaliseCadastral {
  id: number;
  negociacao_id: number;
  step_atual: number;
  is_condominio?: boolean;

  // Step 1 — Identificação
  consumo_compensavel_kwh?: number;
  docs_identificacao?: boolean;
  docs_contrato_social?: boolean;
  docs_faturamento?: boolean;
  docs_faturas_energia?: boolean;
  docs_procuracao?: boolean;
  termo_adesao_assinado?: 'Sim' | 'Não' | 'Pendente';
  pendencias_obs_docs?: string;
  conferencia_docs?: ConferenciaDocs;

  // Step 3 — Crédito / Serasa
  serasa_experian_data?: SerasaExperianData;
  credito_pendencias_financeiras?: boolean;
  credito_valor_pendencias?: number;
  credito_protestos?: boolean;
  credito_valor_protestos?: number;
  credito_cheques_fundo?: boolean;
  credito_valor_cheques?: number;
  credito_score_serasa?: string;
  credito_classificacao_risco?: string;
  credito_risco_sugerido?: string;
  credito_relatorio_serasa_url?: string;
  credito_historico_pagamento_url?: string;
  parecer_credito?: string;

  // Step 4 — Decisão
  decisao_final?: string;
  risco_final?: string;
  ressalvas_condicoes?: string;
  motor_risco_score?: number;
  motor_risco_classificacao?: string;
  analista_id?: string;
  /** Carimbado uma única vez, quando a decisão final é registrada. Nunca sobrescrito. */
  decisao_final_at?: string | null;
  /** Fluxo de Troca de Titularidade durante a Análise Documental. */
  necessita_troca_titularidade?: boolean;
  /**
   * #6428: por que a análise reprovou — código de motivos_reprovacao_analise.
   * Só existe com decisao_final = 'Reprovado' (o banco limpa nas outras).
   * Ver src/lib/motivosReprovacao.ts.
   */
  motivo_reprovacao?: string | null;
  /** Outros motivos que também pesaram, sem o principal. */
  motivos_reprovacao_outros?: string[];
  /** true = classificado depois, lendo o parecer (análises anteriores a 27/10/2026). */
  motivo_reprovacao_retroativo?: boolean;

  /**
   * Liberação de "Aprovado com Ressalvas" sobre pendências das etapas 1-3.
   * Só o departamento Pós Venda grava — o gate real é o trigger
   * trg_analise_liberacao_gate. As pendências NÃO são apagadas: continuam
   * listadas no parecer, com o registro de quem liberou e por quê.
   */
  aprovacao_liberada_por?: string | null;
  aprovacao_liberada_em?: string | null;
  aprovacao_liberada_justificativa?: string | null;

  // Verificação Telefônica (WhatsApp)
  verificacao_telefonica_status?: 'pendente' | 'em_andamento' | 'concluida' | 'nao_realizada';
  verificacao_telefonica_resultado?: string;
  verificacao_telefonica_user_id?: string;
  verificacao_telefonica_at?: string;

  created_at: string;
  updated_at: string;
}

export interface WhatsAppConversa {
  id: number;
  negociacao_id: number;
  analise_cadastral_id?: number;
  user_id: string;
  direcao: 'enviada' | 'recebida';
  tipo_mensagem: 'template' | 'text' | 'image' | 'document' | 'audio' | 'video';
  conteudo?: string;
  template_name?: string;
  media_url?: string;
  telefone_destino: string;
  telefone_empresa: string;
  status: 'sent' | 'delivered' | 'read' | 'failed';
  error_details?: any;
  created_at: string;
  delivered_at?: string;
  read_at?: string;
  user?: { full_name: string; avatar_url?: string };
}

export interface UnidadeGeradora {
  site_id_api: string;
  nome_usina: string;
  capacidade_instalada: number | null;
  status_atual: number | null;
  atualizado_em: string;
  /** growatt | csi | chint | saj. Null nos cadastros antigos: getVendor cai no palpite por formato do site_id_api. */
  fabricante?: string | null;
}

export interface LeituraTelemetria {
  id: string;
  site_id_api: string;
  potencia_atual_w: number;
  energia_hoje_kwh: number;
  /** Aceita null desde 26/08/2026: a SAJ não expõe energia do mês e o trigger
   *  trg_fill_energia_mes_kwh deixa NULL quando não há mês anterior para comparar. */
  energia_mes_kwh: number | null;
  energia_total_kwh: number;
  data_leitura: string;
}

export type SeveridadeAlarme = 'critica' | 'atencao' | 'informativa';

/** Marca de onde veio o alarme. A Chint empurra por webhook; as outras três são
 *  lidas pela edge function coletar-alarmes-geradoras. */
export type FonteAlarme = 'chint' | 'canadian' | 'growatt' | 'saj';

/** De onde saiu o texto do verbete — a tela avisa quando é 'nao_confirmado'.
 *  `norma_tecnica` = conhecimento normativo do setor (ex.: anti-ilhamento é
 *  exigência de qualquer inversor on-grid), quando o fabricante não documenta
 *  mas o comportamento é padronizado e não depende de interpretação nossa. */
export type ProcedenciaTextoAlarme =
    | 'manual' | 'fabricante' | 'norma_tecnica' | 'nao_confirmado';

/** Tradução operacional de um código de alarme (geradoras_alarmes_catalogo).
 *
 *  ⚠️ A chave é (fonte, error_code, error_code_info), não o código sozinho.
 *  O mesmo error_code da FOMware carrega falhas diferentes — GridF.OutLim é
 *  frequência ALTA e BAIXA, GridV.OutLim é sobretensão E falta de fase — e
 *  chavear só pelo código fazia a tela mostrar a falha errada. `error_code_info`
 *  igual a '*' é curinga: vale para qualquer variante daquele código. */
export interface AlarmeCatalogo {
  fonte: FonteAlarme;
  error_code: string;
  error_code_info: string;
  titulo: string;
  o_que_e: string;
  impacto: string;
  acao: string;
  severidade: SeveridadeAlarme;
  fonte_texto: ProcedenciaTextoAlarme;
}

/**
 * Alarme de dispositivo (geradoras_alarmes). Só alarme — geração e potência
 * continuam vindo de LeituraTelemetria.
 */
export interface AlarmeGeradora {
  id: number;
  site_id_api: string;
  site_name: string | null;
  tz_id: string | null;
  gateway_device_sn: string | null;
  mb_device_code: string;
  mb_device_sn: string | null;
  mb_device_name: string | null;
  error_code: string;
  error_code_info: string | null;
  triggered_at: string;
  recebido_em: string;
  fonte: FonteAlarme;
  /** Id do alarme no sistema do fabricante. Null na Chint, que não numera. */
  alarme_id_externo: string | null;
  /** Severidade crua do fabricante, quando ele manda ('Important', 'status:3'). */
  severidade_fabricante: string | null;
  /** Quando a falha acabou. Null = ainda aberta, ou fabricante que não avisa o fim. */
  resolvido_em: string | null;
  resolucao_origem: 'api' | 'inferido' | null;
  /** Quantas vezes a mesma falha repetiu dentro da janela de agrupamento. */
  ocorrencias: number;
  ultima_ocorrencia_em: string | null;
  /** Vem do join com o catálogo; ausente quando o código ainda não foi traduzido. */
  catalogo?: AlarmeCatalogo | null;
}

/**
 * Um evento de alarme como a tela mostra: várias linhas de geradoras_alarmes
 * que são a MESMA falha, na mesma usina, quase no mesmo instante.
 *
 * Existe porque um evento de rede derruba os N inversores da usina de uma vez e
 * a API devolve uma linha por inversor. Sem agrupar, a tela vira uma parede de
 * cards idênticos — foi a queixa que originou tudo isto em 31/08/2026.
 */
export interface EventoAlarme {
  /** Id da linha mais antiga do grupo, usada como chave de React e de expansão. */
  id: number;
  representante: AlarmeGeradora;
  linhas: AlarmeGeradora[];
  /** Inversores distintos atingidos. */
  inversores: string[];
  /** Soma de `ocorrencias` de todas as linhas do grupo. */
  disparos: number;
  inicio: string;
  fim: string | null;
  /** Só é considerado encerrado quando TODAS as linhas do grupo têm fim. */
  encerrado: boolean;
}

export interface GrowattMetric {
  site_id_api: string;
  plant_id: number;
  status_code: number;
  prod_1d_kwh: number | null;
  prod_15d_kwh: number | null;
  prod_30d_kwh: number | null;
  prod_12m_kwh: number | null;
  prod_total_kwh: number;
  last_update_time?: string;
  /** Potência instantânea reportada pela plataforma do fabricante, em W (quando disponível). */
  power_w?: number | null;
  /** Origem no cache (growatt|canadian|chint) — usado para os chips de sincronização. */
  fonte?: string | null;
  /** Quando esta linha do cache foi atualizada pela última vez. */
  atualizado_em?: string | null;
}

/**
 * Um balde da RPC get_geracao_series (hora/dia/mês).
 * `geracao_kwh` null = acumulador resetou ou não havia baseline: é buraco no
 * gráfico, nunca zero. `amostras` sustenta o aviso de hora mal amostrada.
 */
export interface PontoSerieGeracao {
  site_id_api: string;
  /** ISO com fuso (timestamptz). */
  bucket: string;
  geracao_kwh: number | null;
  potencia_media_w: number | null;
  potencia_pico_w: number | null;
  amostras: number;
}

/** Total do período por usina (RPC get_geracao_resumo) — 1 linha por usina. */
export interface ResumoGeracaoUsina {
  site_id_api: string;
  geracao_kwh: number | null;
  potencia_pico_w: number | null;
  /** Dias com leitura. Separa "gerou pouco" de "reportou pouco". */
  dias_com_dado: number;
  melhor_dia_kwh: number | null;
}

/**
 * Usina parada (RPC get_geradoras_sem_reportar).
 * `sem_leitura` = o coletor parou (TI). `sem_geracao` = a leitura chega mas a
 * usina não gera (campo). Separar os dois evita mandar o time errado.
 */
export interface GeradoraSemReportar {
  site_id_api: string;
  nome_usina: string;
  falha: 'sem_leitura' | 'sem_geracao' | 'nunca_reportou';
  ultima_leitura: string | null;
  horas_paradas: number | null;
  ultima_geracao: string | null;
  detalhe: string;
}

/**
 * Tipos de interrupção que a RPC get_interrupcoes_geradoras detecta:
 *   parada           a usina não gerou (acumulador parado, energia não voltou)
 *   parcial          gerou menos de 85% das irmãs do conjunto, por dia
 *   sem_comunicacao  ficou sem mandar dado, mas gerou — não é perda
 *   sem_leitura      o nosso coletor não gravou (TI)
 */
export type TipoInterrupcao = 'parada' | 'parcial' | 'sem_comunicacao' | 'sem_leitura';

/** Alarme do fabricante perto do início de um episódio, já agrupado por código. */
export interface AlarmeNoEpisodio {
  fonte: FonteAlarme;
  error_code: string;
  error_code_info: string | null;
  vezes: number;
  inversores: number;
  primeiro: string;
}

/**
 * Um episódio detectado pela telemetria (RPC get_interrupcoes_geradoras).
 * Nada disto é gravado: a conta é refeita a cada consulta.
 *
 * ⚠️ `energia_nao_gerada_kwh` é ESTIMATIVA contra a mediana das outras usinas
 * (`referencia`: 'irmas' = mesmo conjunto; 'frota' = outro lugar, outro tempo).
 * Só vem preenchida em parada e parcial.
 */
export interface InterrupcaoDetectada {
  site_id_api: string;
  tipo: TipoInterrupcao;
  inicio: string;
  fim: string;
  /** Ainda em curso agora. */
  em_aberto: boolean;
  /** Sem dado ainda para saber se a usina gerou: pode virar "sem comunicação". */
  a_confirmar: boolean;
  /** Já vinha de antes da janela analisada: o início real é anterior. */
  continua_de_antes: boolean;
  /** Período antigo: o episódio segue além da janela analisada. */
  continua_depois: boolean;
  /** Só em `parcial`: dias comparáveis dentro do episódio. */
  dias: number | null;
  horas_sol: number;
  /** Horas de parada total equivalentes (parcial a 75% por 8 h = 2 h). */
  horas_equivalentes: number | null;
  energia_esperada_kwh: number | null;
  energia_nao_gerada_kwh: number | null;
  /** parcial: % das irmãs. sem_comunicacao/sem_leitura: % da energia que voltou. */
  pct_da_referencia: number | null;
  referencia: 'irmas' | 'frota' | null;
  /** Parte do episódio que cai dentro do período pedido. */
  horas_sol_periodo: number;
  horas_equivalentes_periodo: number | null;
  energia_nao_gerada_kwh_periodo: number | null;
  alarmes: AlarmeNoEpisodio[];
}

/**
 * O que uma pessoa registrou (tabela geradoras_interrupcoes_registro): o motivo
 * de um episódio detectado (`classificacao`) ou uma interrupção que o sistema
 * não enxerga (`manual`).
 */
export interface RegistroInterrupcao {
  id: number;
  site_id_api: string;
  tipo: TipoInterrupcao;
  origem: 'classificacao' | 'manual';
  inicio: string;
  fim: string | null;
  /** Id de MOTIVOS_INTERRUPCAO (views/Geradoras/interrupcoes.ts). */
  motivo: string;
  observacao: string | null;
  registrado_por: string;
  created_at: string;
  updated_at: string;
  autor?: { full_name: string | null } | null;
}

/**
 * Uma linha de get_geracao_vs_meta: realizado x esperado de uma usina num mês.
 * `meta_kwh` já vem rateada pelos dias decorridos; `meta_cheia_kwh` é o mês
 * inteiro do laudo. meta null = usina sem PVsyst cadastrado.
 */
export interface PontoVsMetaRow {
  site_id_api: string;
  /** 1º dia do mês (date). */
  mes_ref: string;
  geracao_kwh: number | null;
  dias_com_dado: number;
  dias_no_mes: number;
  /** Fracionário: o dia corrente entra proporcionalmente. */
  dias_decorridos: number;
  /** Havia leitura ANTES do mês. Sem ela, o começo do mês não tem de onde partir. */
  tem_baseline: boolean;
  /** O valor do mês foi corrigido à mão em Config > Geração e Metas. */
  ajuste_manual: boolean;
  /** Dias cujo delta deu negativo (contador resetou) — aí sim há energia perdida. */
  baldes_nulos: number;
  meta_kwh: number | null;
  meta_kwh_p90: number | null;
  meta_cheia_kwh: number | null;
}

/**
 * Uma linha da grade anual de edição (RPC get_geracao_grade_anual).
 * `automatico_kwh` é o que a coleta apurou; `ajuste_kwh` é a correção manual,
 * que vence. `efetivo_kwh` é o que a Análise vai mostrar.
 */
export interface GradeGeracaoMes {
  mes: number;
  automatico_kwh: number | null;
  ajuste_kwh: number | null;
  motivo: string | null;
  efetivo_kwh: number | null;
  dias_com_dado: number;
  dias_no_mes: number;
  dias_decorridos: number;
  /** Mês fechado o suficiente para entrar no percentual da Análise. */
  completo: boolean;
  meta_kwh: number | null;
  meta_p90_kwh: number | null;
}

/** Meta mensal de geração em kWh/kWp (tabela geradoras_metas). */
export interface MetaGeradoraRow {
  site_id_api: string;
  /** 1-12 */
  mes: number;
  /** Normalizado sobre `pnom_kwp_ref` — NÃO sobre capacidade_instalada do cadastro. */
  kwh_por_kwp: number;
  /** Energia esperada do bloco no mês (kWh, P50). Imune a qual potência está cadastrada. */
  kwh_esperado: number | null;
  /** Piso P90, rateado do P90 anual do laudo. */
  kwh_esperado_p90: number | null;
  /** Pnom DC (kWp) DESTE bloco. */
  pnom_kwp_ref: number | null;
  /** Pnom DC da usina inteira no laudo (kWp) — sem ela não dá para reeditar o E_Grid. */
  pnom_kwp_usina: number | null;
  /** Relatório, versão e data de onde a meta saiu. */
  fonte: string | null;
  observacao: string | null;
}

export interface ConfiguracaoSLA {
  id: string;
  tipo: string;
  nome: string;
  tempo_horas: number;
  created_at: string;
  updated_at: string;
}

// ── Programa de Indicações (#6196) ─────────────────────────────────────────────
// Desenho: docs/superpowers/specs/2026-10-19-programa-indicacoes-design.md.
// As telas leem tudo por funções do banco (fn_indicacao_*); estes tipos são o formato
// que elas devolvem.

export type IndicacaoCanal = 'ligacao' | 'whatsapp' | 'visita';
export type IndicacaoResultadoAbordagem = 'indicou' | 'nao_quis' | 'voltar_depois' | 'nao_atendeu';
export type IndicacaoAndamento =
  | 'em_negociacao' | 'aprovada' | 'perdida' | 'nao_vale' | 'recompensa_paga' | 'negociacao_removida';
/** 'ja_na_base' é o que recebe quem não gere o programa, no lugar dos dois primeiros. */
export type IndicacaoMotivoNaoVale = 'ja_cliente' | 'negociacao_ativa' | 'ja_na_base';
export type RecompensaTipo = 'dinheiro' | 'desconto_fatura' | 'brinde';
export type RecompensaStatus = 'a_pagar' | 'suspensa' | 'paga' | 'cancelada';

export interface IndicacaoPrograma {
  id: number;
  nome: string;
  ativo: boolean;
  recompensa_cliente_tipo: RecompensaTipo | null;
  valor_cliente: number | null;
  recompensa_nao_cliente_tipo: 'dinheiro' | 'brinde';
  valor_nao_cliente: number | null;
  descricao_brinde: string | null;
  atualizado_em: string;
}

/** Quem indica, como sai da busca. pessoa_id nulo = cliente ainda nunca abordado. */
export interface IndicacaoPessoaBusca {
  pessoa_id: string | null;
  nome: string;
  cpf_cnpj: string | null;
  whatsapp: string | null;
  cliente: boolean;
  abordagens: number;
  ultima_abordagem_em: string | null;
}

/** Linha da aba Pessoas. */
export interface IndicacaoPessoaResumo extends IndicacaoPessoaBusca {
  ucs: number;
  cidade: string | null;
  ultima_abordagem_por: string | null;
  ultimo_resultado: IndicacaoResultadoAbordagem | null;
  indicacoes: number;
  aprovadas: number;
  total: number;
}

export interface IndicacaoItem {
  id: number;
  registrado_em: string;
  registrado_por_nome: string | null;
  forma: 'abordagem' | 'espontanea';
  indicador_nome: string;
  pessoa_id: string;
  abordagem_id: number | null;
  indicado_nome: string;
  indicado_whatsapp: string | null;
  indicado_cidade: string | null;
  vale: boolean;
  motivo_nao_vale: IndicacaoMotivoNaoVale | null;
  andamento: IndicacaoAndamento;
  negociacao_id: number | null;
  responsavel_nome: string | null;
  destino: 'registrante' | 'rodizio' | null;
  recompensa_status: RecompensaStatus | null;
  total?: number;
}

export interface IndicacaoAbordagemItem {
  id: number;
  abordado_em: string;
  abordado_por_nome: string | null;
  canal: IndicacaoCanal;
  resultado: IndicacaoResultadoAbordagem;
  observacao: string | null;
}

export interface IndicacaoFicha {
  pessoa: {
    id: string | null;
    nome: string;
    cpf_cnpj: string | null;
    whatsapp: string | null;
    chave_pix: string | null;
    cliente: boolean;
    ucs: number;
    cidade: string | null;
    pode_editar_pagamento: boolean;
  };
  abordagens: IndicacaoAbordagemItem[];
  indicacoes: IndicacaoItem[];
}

export interface IndicacaoChecagem {
  whatsapp: string | null;
  vale: boolean;
  motivo: IndicacaoMotivoNaoVale | 'auto_indicacao' | 'whatsapp_invalido' | null;
  conflito?: { negociacao_id: number; status: string; responsavel_nome: string | null } | null;
  anterior?: { negociacao_id: number; status: string; em: string } | null;
}

export interface IndicacaoResultadoIndicado {
  nome: string;
  vale: boolean;
  motivo: IndicacaoMotivoNaoVale | null;
  negociacao_id: number | null;
  destino: 'registrante' | 'rodizio' | null;
  responsavel_nome: string | null;
  conflito_responsavel_nome: string | null;
}

export interface IndicacaoResultadoRegistro {
  abordagem_id: number;
  pessoa_id: string;
  indicados: IndicacaoResultadoIndicado[];
}

export interface IndicacaoResumoCliente {
  pessoa_id: string;
  abordagens: number;
  ultima_abordagem_em: string | null;
  ultima_abordagem_por: string | null;
  indicacoes: number;
  aprovadas: number;
}

export interface IndicacaoDaNegociacao {
  indicacao: {
    id: number;
    pessoa_id: string;
    indicador_nome: string;
    registrado_em: string;
    registrado_por_nome: string | null;
    forma: 'abordagem' | 'espontanea';
  } | null;
  resumo_cliente: IndicacaoResumoCliente | null;
  pode_registrar_espontanea: boolean;
}

export interface IndicacaoKpisMes {
  abordagens: number;
  indicacoes: number;
  aprovadas: number;
  a_pagar_qtd: number | null;
  a_pagar_valor: number | null;
  a_definir_qtd: number | null;
}

export interface IndicacaoRecompensa {
  id: number;
  indicacao_id: number;
  pessoa_id: string;
  indicador_nome: string;
  indicador_cpf_cnpj: string | null;
  indicador_chave_pix: string | null;
  tipo_indicador: 'cliente' | 'nao_cliente';
  tipo_recompensa: RecompensaTipo | null;
  valor: number | null;
  status: RecompensaStatus;
  gerada_em: string;
  paga_em: string | null;
  paga_por_nome: string | null;
  comprovante_path: string | null;
  observacao: string | null;
  alerta: string | null;
  indicado_nome: string;
  negociacao_id: number | null;
}

export interface Parceiro {
  id: string;
  created_at: string;
  nome: string;
  whatsapp: string;
  cnpj: string;
  modelo_atuacao: string;
  cidade: string;
  status: 'novo' | 'em_analise' | 'aprovado' | 'rejeitado';
}

// ==========================================
// FOLHA DE PAGAMENTO (Payroll / Commissions)
// ==========================================

export interface TarifaEnergia {
  id: number;
  valor: number;
  vigencia_inicio: string;
  descricao?: string;
  created_at: string;
  created_by?: string;
}

export interface MetaVendedor {
  id: number;
  vendedor_id: string;
  mes: number;
  ano: number;
  meta_kwh: number;
  salario_base: number;
  created_at: string;
  updated_at: string;
  vendedor?: Profile;
}

export type ComissaoParcelaStatus = 'pendente' | 'aprovado' | 'pago' | 'cancelado';
export type ComissaoParcela2Status = 'aguardando_primeira_fatura' | 'pendente' | 'aprovado' | 'pago' | 'cancelado';
export type GrupoAStatus = 'pendente_aprovacao_gerente' | 'aprovado_manual' | 'rejeitado';

export interface ComissaoParceiro {
  id: number;
  parceiro_id: string;
  negociacao_id: number;
  consumo_medio_kwh: number;
  tarifa_energia_valor: number;
  tarifa_energia_id: number;
  faixa_comissao_percentual: number;
  valor_base_comissao: number;
  parcela_1_valor: number;
  parcela_1_data_prevista?: string;
  parcela_1_status: ComissaoParcelaStatus;
  parcela_1_aprovado_por?: string;
  parcela_1_aprovado_em?: string;
  parcela_1_pago_por?: string;
  parcela_1_data_pagamento?: string;
  parcela_2_valor: number;
  parcela_2_data_prevista?: string;
  parcela_2_status: ComissaoParcela2Status;
  parcela_2_aprovado_por?: string;
  parcela_2_aprovado_em?: string;
  parcela_2_pago_por?: string;
  parcela_2_data_pagamento?: string;
  is_grupo_a: boolean;
  grupo_a_status?: GrupoAStatus;
  grupo_a_aprovado_por?: string;
  grupo_a_aprovado_em?: string;
  grupo_a_observacoes?: string;
  observacoes?: string;
  /** null = regra antiga; '2026-10' = bonificação da regra de 26/10/2026 (src/lib/bonificacaoParceiro.ts). */
  regra?: string | null;
  /** Regra nova: 1º dia do mês do aceite (YYYY-MM-01, fuso de São Paulo). */
  mes_aceite?: string | null;
  /** Regra nova: desconto do consumidor em % (20 a 35). */
  desconto_percentual?: number | null;
  /** Regra nova: parte da bonificação que fica com o parceiro (1 = plano padrão). */
  redutor?: number | null;
  /** Regra nova: consumo × R$ 1,16 × (1 − desconto). */
  fatura_com_desconto?: number | null;
  /** Regra nova: faixa do mês do parceiro (F1 a F6); a % fica em faixa_comissao_percentual. */
  faixa_mes?: string | null;
  /** Regra nova: kWh somados no mês pelo parceiro — o que define a faixa. */
  kwh_mes?: number | null;
  /** Regra nova: por que o consumidor não entra na conta (valores zerados). */
  motivo_fora?: string | null;
  created_at: string;
  updated_at: string;
  parceiro?: Profile;
  negociacao?: Negociacao;
}

export type ComissaoVendedorStatus = 'calculado' | 'aprovado' | 'pago';

export interface ComissaoVendedor {
  id: number;
  vendedor_id: string;
  mes: number;
  ano: number;
  total_kwh_vendido: number;
  meta_kwh: number;
  percentual_atingimento: number;
  percentual_comissao: number;
  tarifa_energia_valor: number;
  tarifa_energia_id: number;
  valor_comissao_kwh: number;
  valor_bonus: number;
  salario_base: number;
  valor_total: number;
  status: ComissaoVendedorStatus;
  observacoes?: string;
  // Nova Política Comercial (jul/2026) — componentes do fechamento v2.
  // 'interno_v1' (set/2026) é o Comercial Interno — motor totalmente separado
  // (src/lib/comissaoInternaV1.ts), não uma variação do v1/v2 do vendedor externo.
  /** pos_venda_indicacao = linha do pós-venda só com a comissão de indicação (#6236). */
  politica?: 'v1' | 'v2' | 'interno_v1' | 'pos_venda_indicacao';
  valor_ativacao?: number;
  valor_threshold?: number;
  valor_rede_parceiros?: number;
  valor_bonus_safra?: number;
  valor_recorrencia?: number; // EM ANÁLISE — permanece 0 na política v2 atual
  valor_clawback?: number;
  sla_cumprido?: boolean | null;
  sla_detalhe?: { visitas: number; posVenda: number; fechamentos: number; parceiros: number; ajusteManual?: boolean } | null;
  safra_detalhe?: { safraMes: number; safraAno: number; totalClientes: number; pagosNoMes: number; pct: number; clientes: Array<{ negociacaoId: number; pago: boolean }> } | null;
  carteira_detalhe?: Record<string, any> | null;
  detalhe_calculo?: Record<string, any> | null;
  // Audit trail
  aprovado_por?: string;
  aprovado_em?: string;
  pago_por?: string;
  pago_em?: string;
  comprovante_url?: string;
  comprovante_nome?: string;
  created_at: string;
  updated_at: string;
  vendedor?: Profile;
  negociacoes_detalhe?: ComissaoVendedorNegociacao[];
}

export interface ComissaoVendedorNegociacao {
  id: number;
  comissao_vendedor_id: number;
  negociacao_id: number;
  consumo_medio_kwh: number;
  valor_bonus: number;
  is_grupo_a: boolean;
  grupo_a_status?: GrupoAStatus;
  grupo_a_aprovado_por?: string;
  grupo_a_aprovado_em?: string;
  // Nova Política Comercial (jul/2026)
  multiplicador_final?: number | null;
  valor_ativacao?: number;
  parcela_1_valor?: number | null;
  parcela_1_status?: string | null;
  parcela_2_valor?: number | null;
  parcela_2_status?: string | null;
  clawback_status?: string | null;
  clawback_em?: string | null;
  created_at: string;
  negociacao?: Negociacao;
}

export type RateioModalidade = 'percentual_fixo' | 'ordem_prioridade';
export type RateioStatus = 'rascunho' | 'aguardando_protocolo' | 'protocolado' | 'conciliado';
export type RateioGargalo = 'reducao_proporcional' | 'priorizacao_seletiva';

export interface RateioFalhaHistorico {
  reportado_por?: string | null;
  reportado_por_nome?: string | null;
  reportado_em: string;
  motivo: string;
}

export type RateioStatusAprovacaoUg = 'pendente' | 'aprovada' | 'rejeitada';

export type ColunaPlanilhaRateio = 'uc' | 'outro_numero_uc' | 'titular' | 'cpf_cnpj' | 'percent' | 'energia_alvo_kwh';

export interface Distribuidora {
  id: string;
  nome: string;
  email_protocolo?: string | null;
  dia_leitura_padrao?: number | null;
  webhook_protocolo_url?: string | null;
  colunas_planilha?: ColunaPlanilhaRateio[];
  ativo: boolean;
  created_at: string;
  created_by?: string | null;
  updated_at: string;
}

export interface RateioAncoraTransferencia {
  id: string;
  rateio_unidade_geradora_id: string;
  unidade_consumidora_destino_id: number;
  montante_kwh: number;
  data_retorno_agendada: string;
  status: 'ativa' | 'retornada';
  solicitado_por?: string | null;
  data_solicitacao: string;
  retornado_por?: string | null;
  retornado_em?: string | null;
  created_at: string;
  updated_at: string;
  // presentes quando o select faz embed via FK
  rateio_unidades_geradoras?: { nome: string } | null;
  unidades_consumidoras?: { numero_uc: string; nome_cliente: string | null } | null;
}

export interface RateioUnidadeGeradora {
  id: string;
  nome: string;
  distribuidora?: string | null; // legado (texto livre) — não usado pela UI a partir da Fatia 5, ver distribuidora_id
  distribuidora_id?: string | null;
  status_aprovacao: RateioStatusAprovacaoUg;
  aprovado_por?: string | null;
  aprovado_em?: string | null;
  motivo_rejeicao?: string | null;
  capacidade_kwp?: number | null;
  // Vínculo com a telemetria real (Growatt/Chint/Canadian/SAJ, unidades_geradoras)
  // — não é FK (tabela de telemetria não tem UUID), é vínculo por site_id_api (texto).
  telemetria_marca?: 'growatt' | 'chint' | 'canadian' | 'saj' | null;
  telemetria_site_id?: string | null;
  // Número de UC da fatura própria da UG (medidor de injeção) — usado pra
  // buscar geracao_unidade_geradora_N em faturas já extraídas.
  numero_uc_geradora?: string | null;
  // Número ANTIGO da UC geradora — é o que a planilha do Rateio Antigo traz em
  // rateios_credito_itens.uc_geradora. numero_uc_geradora guarda o novo (12 dígitos).
  numero_uc_geradora_antigo?: string | null;
  ativo: boolean;
  created_at: string;
  created_by?: string | null;
  updated_at: string;
  distribuidora_config?: Distribuidora | null; // presente quando o select faz embed via FK
}

export interface Rateio {
  id: string;
  rateio_unidade_geradora_id: string;
  mes_referencia: string;
  excedente_alocavel_kwh: number;
  modalidade: RateioModalidade;
  status: RateioStatus;
  usuario_id?: string | null;
  usuario_nome?: string | null;
  ancora_kwh: number;
  gargalo?: RateioGargalo | null;
  aprovado_por?: string | null;
  aprovado_em?: string | null;
  protocolado_em?: string | null;
  protocolado_via?: 'email' | 'manual' | null;
  conciliado_em?: string | null;
  historico_falhas: RateioFalhaHistorico[];
  planilha_url?: string | null;
  // Conciliação manual (Fatia 8 — Seção 13.4/Tela 4).
  excedente_faturado_kwh?: number | null;
  divergencia_percent?: number | null;
  espelho_registrado_em?: string | null;
  espelho_registrado_por?: string | null;
  ultimo_lembrete_espelho_em?: string | null;
  created_at: string;
  updated_at: string;
}

export interface RateioAncoraSaldoUg {
  rateio_unidade_geradora_id: string;
  saldo_ancora_kwh: number;
  ultima_atualizacao: string | null;
}

export interface RateioItem {
  id: string;
  rateio_id: string;
  unidade_consumidora_id: number;
  uc: string;
  outro_numero_uc?: string | null;
  nome?: string | null;
  cpf_cnpj?: string | null;
  percent_rateio: number;
  energia_alvo_kwh: number;
  locked: boolean;
  ordem?: number | null;
  created_at: string;
  updated_at: string;
}

// Fluxo antigo de upload manual de planilha (RateioCreditosView) — anterior ao
// Módulo de Rateio (Simulador/Dashboard/Âncora). Tabelas rateios_credito/
// rateios_credito_itens mantidas intocadas (ver src/services/api.ts), ainda
// com dados históricos reais consumidos por views/Financeiro/FinanceiroLayout.tsx.
export interface RateioCredito {
  id: string;
  mes_referencia: string;
  usuario_id: string;
  usuario_nome: string;
  created_at: string;
}

export interface RateioCreditoItem {
  id: string;
  rateio_id: string;
  uc?: string;
  outro_numero_uc?: string;
  status?: string;
  cpf_cnpj?: string;
  nome?: string;
  uc_geradora?: string;
  percent_rateio?: number | null;
  dia_apresentacao?: string;
  dia_vencimento?: string;
  dia_leitura_anterior?: string;
  created_at: string;
}

export type RateioAjusteTipo = 'alterar_percentual' | 'trocar_geradora' | 'remanejar_saldo' | 'incluir' | 'excluir' | 'outro';
export type RateioAjusteStatus = 'pendente' | 'enviado' | 'aplicado' | 'cancelado';

/**
 * Pedido de ajuste de rateio de uma UC (painel Saldos das UCs). Só registro: o %
 * que vale continua vindo da planilha do Rateio Antigo. Percentuais em FRAÇÃO da
 * geração da usina, como `RateioCreditoItem.percent_rateio` (0.0004 = 0,04%).
 */
export interface RateioAjusteUc {
  id: string;
  unidade_consumidora_id?: number | null;
  uc_numero: string;
  tipo: RateioAjusteTipo;
  percent_atual?: number | null;
  percent_novo?: number | null;
  ug_atual?: string | null;
  ug_nova?: string | null;
  ug_remanejamento?: string | null;
  mes_rateio_origem?: string | null;
  mes_rateio_alvo?: string | null;
  motivo?: string | null;
  status: RateioAjusteStatus;
  criado_por?: string | null;
  criado_por_nome?: string | null;
  status_atualizado_por_nome?: string | null;
  status_atualizado_em?: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Etapas da régua automática. 'D+10' só existe para os 639 registros históricos
 * anteriores a 11/08/2026 — desde então o 3º aviso automático é o 'D+7' e o
 * D+10 virou contato humano (ver CobrancaAcaoManual).
 *
 * Reunião de 23/10/2026: os avisos antes do vencimento passam a D-3 e D0. O 'D-1'
 * segue até o D0 entrar no ar e depois fica como histórico.
 */
export type TipoLembreteAutomatico = 'D-3' | 'D0' | 'D-1' | 'D+3' | 'D+7' | 'D+10';

export interface LembreteCobrancaLog {
  id: string;
  fatura_id: number;
  uc_codigo: string;
  /** Coluna GERADA — somente leitura. Espelha Fatura.uc_codigo_norm. */
  uc_codigo_norm?: string | null;
  tipo_lembrete: TipoLembreteAutomatico;
  template_name: string;
  whatsapp_destino: string;
  nome_cliente: string | null;
  valor_boleto: number | null;
  vencimento_boleto: string | null;
  mes_referencia: string | null;
  /** 'suprimido': o D+7 não saiu porque já houve contato humano (23/10/2026). */
  status: 'enviado' | 'falhou' | 'reenviado' | 'suprimido';
  error_details: string | null;
  uaizychat_response: Record<string, unknown> | null;
  enviado_em: string;
  reenviado_por_nome: string | null;
  reenviado_em: string | null;
  created_at: string;
}

export interface LembretesKPIs {
  enviadosHoje: number;
  taxaSucesso: number;
  falhasHoje: number;
  semWhatsapp: number;
}

// ─────────────────────────────────────────
// "Sincronizar status sem gerar boleto" — troca status de fatura já
// renegociada por fora do sistema, sem boleto novo, com aprovação de
// gerente/adm e checagem leve da Vick sobre o motivo. Ver
// docs/superpowers/specs/2026-09-02-sincronizacao-status-fatura-design.md.
// ─────────────────────────────────────────

export type FaturaStatusSyncDecisao = 'pendente' | 'aprovado' | 'rejeitado';

export interface FaturaStatusSyncRequest {
  id: string;
  fatura_id: number;
  status_atual: string;
  status_alvo: string;
  motivo: string;
  solicitado_por_id: string;
  solicitado_por_nome: string;
  solicitado_em: string;
  vick_faz_sentido: boolean | null;
  vick_comentario: string | null;
  decisao: FaturaStatusSyncDecisao;
  decidido_por_id: string | null;
  decidido_por_nome: string | null;
  decidido_em: string | null;
  motivo_rejeicao: string | null;
  created_at: string;
  // Resolvido no client (join manual com faturas, não é coluna)
  fatura?: Pick<Fatura, 'id' | 'uc_codigo' | 'cliente_nome' | 'mes_referencia' | 'valor_boleto_vigor' | 'vencimento_boleto'> | null;
}

// ─────────────────────────────────────────
// Fila de contato humano da régua de cobrança (D+4, D+10, D+17, D+24)
// ─────────────────────────────────────────

/** D+4 entrou em 02/10/2026 (#6145): primeiro contato humano, antes do D+10. */
export type CobrancaAcaoEtapa = 'D+4' | 'D+10' | 'D+17' | 'D+24';
export type CobrancaAcaoStatus = 'pendente' | 'concluido' | 'dispensado';
export type CobrancaAcaoCanal = 'whatsapp' | 'ligacao' | 'email' | 'presencial' | 'outro';

/** Snapshot de uma fatura dentro da ação — gravado no momento em que a etapa nasce. */
export interface CobrancaAcaoFatura {
  fatura_id: number;
  uc_codigo: string;
  valor: number | null;
  vencimento: string | null;
}

/**
 * Uma etapa de contato humano para um cliente. Uma linha por CLIENTE por etapa
 * por vencimento — o contato é um só mesmo que o cliente tenha várias UCs
 * vencidas. Criada pelo cron `popular_cobranca_acao_manual()`; a tela só lê e
 * registra a execução.
 *
 * O status de NEGOCIAÇÃO (negociando/negociado/negativado) não mora aqui — ele
 * continua em `cobranca_clientes`, que já tem timeline própria.
 */
export interface CobrancaAcaoManual {
  id: string;
  chave_agrupamento: string;
  cpf_cnpj_cliente: string | null;
  cliente_nome: string | null;
  etapa: CobrancaAcaoEtapa;
  /** Vencimento do boleto que originou a etapa. */
  data_referencia: string;
  faturas: CobrancaAcaoFatura[];
  valor_total: number;
  whatsapp_destino: string | null;
  status: CobrancaAcaoStatus;
  responsavel_id: string | null;
  executado_por: string | null;
  executado_em: string | null;
  canal: CobrancaAcaoCanal | null;
  observacao: string | null;
  dispensado_motivo: string | null;
  created_at: string;
  updated_at: string;
  // Resolvidos no client (não são colunas)
  responsavel_nome?: string | null;
  executado_por_nome?: string | null;
  status_negociacao?: CobrancaStatusNegociacao | null;
  /** Notificação extrajudicial anterior do mesmo CPF/CNPJ (qualquer UC), se existir. */
  notificacao_previa?: { enviado_cliente_em: string; resultado_verificacao: string | null } | null;
}

export interface AcoesManuaisKPIs {
  pendentesD4: number;
  pendentesD10: number;
  pendentesD17: number;
  pendentesD24: number;
  valorPendente: number;
  concluidasHoje: number;
}

// ─────────────────────────────────────────
// ESTEIRA DE COBRANÇA E DOCUMENTOS
// ─────────────────────────────────────────

/**
 * A esteira junta, numa fila só, o que hoje vive espalhado por cinco tabelas e
 * só chega à pessoa pelo sininho — que avisa mas não guarda estado: lida não é
 * feita. Aqui cada demanda tem fila, responsável, idade e desfecho.
 *
 * Nada nasce nesta tela. Toda linha é derivada do estado das tabelas de origem;
 * as ações reabrem os modais que já existem.
 */
export type EsteiraGrupo = 'regua' | 'notificacoes' | 'documentos' | 'negativacao' | 'negociacao';

export type EsteiraFila =
  // Régua de inadimplência — contato humano (cobranca_acao_manual)
  | 'regua_d4'
  | 'regua_d10'
  | 'regua_d17'
  | 'regua_d24'
  // Notificação extrajudicial de mora
  | 'mora_sem_assinatura'
  | 'mora_sem_envio'
  | 'mora_prazo_vencido'
  // Comunicado de advertência
  | 'advertencia_sem_assinatura'
  | 'advertencia_sem_envio'
  | 'advertencia_prazo_vencido'
  // Distrato / termo de cancelamento.
  // `distrato_em_aberto` é o estado ANTERIOR à reconciliação com o Autentique —
  // um termo recém-gerado cai aqui até `reconciliar-distrato` passar. Sem ele,
  // distrato novo sumiria da tela até o cron rodar.
  | 'distrato_em_aberto'
  | 'distrato_aguardando_vigor'
  | 'distrato_aguardando_cliente'
  | 'distrato_assinado_sem_envio'
  // Negativação e negociação (cobranca_clientes)
  | 'negativacao_pendente'
  // Negativado que regularizou (pagou ou negociou): falta retirar do SPC (#6147)
  | 'negativacao_retirar'
  | 'followup_vencido'
  // Teve contato e ficou sem data de retorno nem status que encerre (#6165)
  | 'sem_retorno'
  // Deve, tem dono e ninguém falou com ele por esta dívida — fora da janela
  // da régua (D+4 a D+24) ninguém via (07/10/2026)
  | 'sem_contato'
  | 'sem_responsavel'
  // Encaminhado para a notificação extrajudicial pelo "Gerenciar Cobrança"
  | 'notificacao_extrajudicial'
  // Negativado no SPC e ainda devendo: só acompanhamento, não é tarefa (#6209)
  | 'negativado_spc';

export type EsteiraOrigem =
  | 'cobranca_acao_manual'
  | 'notificacao_mora_logs'
  | 'comunicado_advertencia_logs'
  | 'termo_cancelamento_logs'
  | 'cobranca_clientes';

/** Uma demanda da esteira, já normalizada — venha ela de onde vier. */
export interface DemandaEsteira {
  /** `${origem}:${origem_id}` — estável entre recargas, serve de key do React. */
  id: string;
  origem: EsteiraOrigem;
  origem_id: string;
  fila: EsteiraFila;
  grupo: EsteiraGrupo;

  cliente_nome: string | null;
  cpf_cnpj_cliente: string | null;
  uc_numero: string | null;
  /**
   * Todas as UCs do cliente que o card mostra (#6201): as que estão devendo
   * ou, se não deve nada, as do cadastro pelo CPF. `uc_numero` é a primeira.
   */
  ucs?: Array<{ numero: string; cancelada?: boolean }>;
  valor: number | null;

  /** Quando o registro de origem nasceu. */
  criado_em: string;
  /**
   * A data que manda no atraso — não é sempre `criado_em`. Para "assinado sem
   * envio" o relógio começa na assinatura, não na geração do documento.
   */
  referencia_em: string;
  /** Prazo a cumprir, quando o documento tem um (5 DU da mora, 15 dias da advertência). */
  prazo_em: string | null;
  /** Dias corridos desde `referencia_em`. */
  dias_parado: number;
  /**
   * Idade da pendência (desde o vencimento, a data marcada ou o documento) —
   * o que `dias_parado` era antes de #6211. Preenchido só quando alguém agiu
   * depois disso e o "parado" passou a contar da última ação.
   */
  dias_atraso?: number;
  /** Última ação de verdade no card (contato, mudança de status, andamento). */
  ultima_acao_em?: string | null;
  /** O dono veio do último contato registrado, não da carteira (#6211). */
  dono_pelo_contato?: boolean;
  /** Trabalho registrado hoje neste cliente/card: quem e o quê (#6211). */
  trabalho_hoje?: Array<{ por: string | null; por_id?: string | null; acao: string; em: string }>;
  /** Faturas vencidas em aberto do cliente, ao vivo (#6211). */
  faturas_abertas?: Array<{ fatura_id: number; uc_codigo: string | null; mes_referencia: string | null; valor: number | null; vencimento: string | null; distrato?: boolean }>;

  responsavel_id: string | null;
  responsavel_nome?: string | null;
  status: CobrancaAcaoStatus;

  /**
   * Status da negociação com o cliente, quando há CPF/CNPJ. Vive em
   * `cobranca_clientes` e é só exibido aqui — quem muda é o
   * PainelNegociacaoCobranca, via ClienteCobrancaModal.
   */
  status_negociacao?: CobrancaStatusNegociacao | null;

  /**
   * Quem assumiu e qual o último andamento. Só existe nas filas DERIVADAS
   * (documento e negociação) — a régua guarda isso na própria
   * `cobranca_acao_manual`. `null` quando ninguém assumiu ainda.
   */
  atribuicao?: EsteiraAtribuicao | null;

  /** PDF do documento, quando existe. */
  url_documento: string | null;
  /** Frase curta explicando por que a demanda está nesta fila. */
  detalhe: string | null;

  /**
   * Fatos que ESTA fila precisa mostrar e que não cabem nos campos comuns —
   * modelo do distrato, motivo do desligamento, quem mandou negativar, o que
   * foi combinado com o cliente. Cada normalizador decide o que é relevante,
   * porque o que importa num distrato não é o que importa numa negativação.
   *
   * Existe para a pessoa não precisar sair da tela para saber o básico.
   */
  contexto?: Array<{ rotulo: string; valor: string }>;

  /**
   * Contato do cliente, quando a origem tem. A régua já trazia isso
   * (`whatsapp_destino`); as filas de documento tinham o dado gravado e não
   * mostravam — quem ia cobrar uma assinatura precisava sair procurar.
   */
  contato_whatsapp?: string | null;
  contato_email?: string | null;

  /** Linha original, para os modais que precisam do registro inteiro. */
  bruto?: unknown;

  /**
   * Quando o card junta várias pendências do MESMO cliente com a MESMA pessoa
   * (ver `agruparPorCliente`): todas elas, a principal incluída. Antes cada
   * vencimento e cada etapa virava um card — a Roberta tinha nove (#6147).
   */
  itens?: DemandaEsteira[];
  /** As outras filas que o card agrupado também cobre, sem a principal. */
  outras_filas?: EsteiraFila[];
  /**
   * Card agrupado: as etapas que estão com OUTRA pessoa que não o responsável
   * do card (#6209 — a D+17 com a Bruna num cliente da carteira do João). O
   * card aparece em "Meus itens" de todas elas.
   */
  donos_etapas?: Array<{ id: string; nome: string | null; filas: EsteiraFila[] }>;
  /**
   * Só acompanhamento (negativado no SPC): não é tarefa, fica fora de "Paradas
   * +7d", "Sem dono" e do valor pendente, e vai para o fim da lista.
   */
  acompanhamento?: boolean;
  /** Desde quando o cliente está no SPC, quando está (sem retirada). Vira selo no card. */
  negativado_spc_em?: string | null;
  /** Próximo contato agendado com o cliente (cobranca_clientes). */
  proximo_contato_em?: string | null;
  /**
   * Retorno agendado para depois de hoje: o cliente sai do painel até a data
   * e volta sozinho nela. Fica fora da lista padrão e dos números.
   */
  agendado?: boolean;
  /**
   * O distrato toma conta: card de distrato ou cobrança de fatura de UC com
   * distrato. Fica fora da lista padrão e só aparece no filtro "Com distrato"
   * (pedido da Dhaiane, 07/10/2026).
   */
  com_distrato?: boolean;
}

/**
 * Uma linha de `esteira_atribuicoes`: quem assumiu a demanda e o que foi feito
 * por último.
 *
 * Não há campo de "concluído" de propósito. As filas derivadas se resolvem pelo
 * dado de origem (uma notificação sai de "aguardando assinatura" quando o
 * Autentique confirma), nunca por alguém marcar um checkbox — marcar à mão
 * esconderia trabalho não feito.
 */
export interface EsteiraAtribuicao {
  id: string;
  origem: EsteiraOrigem;
  origem_id: string;
  responsavel_id: string | null;
  assumido_em: string | null;
  assumido_por: string | null;
  ultima_acao_em: string | null;
  ultima_acao_por: string | null;
  observacao: string | null;
  created_at: string;
  updated_at: string;
  // Resolvidos no client (não são colunas)
  responsavel_nome?: string | null;
  ultima_acao_por_nome?: string | null;
}

export interface EsteiraKPIs {
  porGrupo: Record<EsteiraGrupo, number>;
  porFila: Record<EsteiraFila, number>;
  /** Soma dos valores em aberto das demandas que têm valor. */
  valorPendente: number;
  /** Quantas demandas estão paradas há mais de 7 dias. */
  paradasMais7: number;
  /**
   * Demandas que alguém tocou hoje — régua concluída/dispensada mais andamento
   * registrado nas filas derivadas. Herda o KPI "concluídas hoje" da ManuaisTab,
   * que é como a gerência enxerga se o dia rendeu.
   */
  trabalhadasHoje: number;
  /** Demandas pendentes que ninguém assumiu. */
  semDono: number;
}

export interface MarketingMetaAdCreative {
  ad_id: string;
  ad_name: string;
  creative_id: string;
  thumbnail_url: string;
  image_url: string;
  video_id: string;
  video_url: string;
  body: string;
  title: string;
  call_to_action: string;
  updated_at: string;
}

export interface MarketingMetaAdMetric {
  id: string;
  date: string;
  ad_id: string;
  ad_name: string;
  adset_name: string;
  campaign_name: string;
  spend: number;
  impressions: number;
  reach: number;
  frequency: number;
  cpm: number;
  link_clicks: number;
  conversations_started: number;
  leads: number;
  landing_page_views: number;
  results_total: number;
  cost_per_result: number;
  video_avg_time_watched: number;
  video_p25_watched: number;
  video_p50_watched: number;
  video_p75_watched: number;
  video_p95_watched: number;
  video_p100_watched: number;
  actions_raw: any[];
  created_at: string;
  updated_at: string;
}

export interface MarketingMetaDailyMetric {
  id: string;
  date: string;
  campaign_name: string;
  spend: number;
  impressions: number;
  reach: number;
  frequency: number;
  cpm: number;
  link_clicks: number;
  conversations_started: number;
  leads: number;
  landing_page_views: number;
  video_views: number;
  results_total: number;
  cost_per_conversation: number;
  actions_raw: any[];
  created_at: string;
  updated_at: string;
}

// ===== Extração de Faturas (módulo determinístico) =====
// Espelha a saída do parser em supabase/functions/_shared/fatura-parser/types.ts.
// Mantido aqui para o frontend (Vite) sem depender de imports da pasta Deno.

export type PostoHorario = 'FP' | 'P' | 'HR' | 'UNICO' | '';

export interface ExtItemFatura {
  descricao: string;
  grupo_secao: string;
  posto: PostoHorario;
  unidade: string;
  qtd: string;
  tarifa_unit: string;
  preco_unit_com_tributos: string;
  valor: string;
  uc_geradora: string;
  tipo_gd: string;
  base_icms: string;
  aliquota_icms: string;
  icms_valor: string;
  pis_cofins: string;
}

export interface ExtTributo { tipo: 'PIS_PASEP' | 'ICMS' | 'COFINS'; base: string; aliquota: string; valor: string; }
export interface ExtSceeInfo { uc_geradora: string; geracao_ciclo_kwh: string; ciclo_mes: string; excedente_recebido_kwh: string; percent_rateio: string; }
export interface ExtHistoricoMes { mes_ano: string; consumo_faturado: string; dias: string; tipo: string; }
export interface ExtSaldos { credito_recebido: string; saldo: string; saldo_expirar_30: string; saldo_expirar_60: string; ufer: string; }
export interface ExtMedidorLinha { medidor: string; grandeza: string; posto: string; leitura_anterior: string; leitura_atual: string; constante: string; consumo: string; }

export interface FaturaEstruturada {
  cabecalho: Record<string, any>;
  tributos: ExtTributo[];
  itens: ExtItemFatura[];
  scee: ExtSceeInfo[];
  historico: ExtHistoricoMes[];
  medidor?: ExtMedidorLinha[];
  saldos: ExtSaldos;
  meta: { parser_version: string; ocr_confidence: number; avisos: string[]; paginas?: number };
}

export interface ReconciliacaoCheck {
  tipo: 'critico' | 'alerta' | 'ok' | 'info';
  categoria: string;
  campo: string;
  mensagem: string;
  valor_declarado?: string;
  valor_esperado?: string;
}

export interface ReconciliacaoResultado {
  checagens: ReconciliacaoCheck[];
  status: 'ok' | 'revisar' | 'falhou';
  score: number;
}

/** Onde o robô de extração foi acionado (extracoes_faturas.origem). É o que a tela
 *  /gestao/extracao/historico agrupa. Só 'gestao' usa lote_id — todas as outras
 *  origens gravam linhas soltas. */
export type OrigemExtracao =
  | 'gestao'
  | 'robo-manual'
  | 'robo-assinatura'
  | 'robo-historico'
  | 'analise-cadastral'
  | 'crm';

/** Abas da planilha da extração (espelham as abas do .xlsx exportado). */
export type AbaPlanilhaExtracao = 'faturas' | 'itens' | 'medidor' | 'auditoria' | 'erros';

/** Uma célula corrigida à mão na planilha. `label` é o rótulo da coluna no momento em
 *  que foi salva — serve de conferência ao reaplicar (coluna que mudou de posição não
 *  recebe valor de outra). */
export interface CorrecaoCelulaPlanilha { label: string; valor: string | number; }

/** Camada de correções manuais de UMA extração (`extracoes_faturas.correcoes_planilha`).
 *  Não sobrescreve `dados_estruturados` — o que o robô extraiu continua lá para
 *  auditoria; a planilha e o Excel aplicam esta camada por cima.
 *  Chave da célula: "<âncora do sub-registro>:<coluna>" — a âncora é '0' na aba Faturas
 *  e uma assinatura do conteúdo do item/medidor/checagem nas demais (ver RowOwner em
 *  views/Gestao/Extracao/planilha/faturaGridModel.ts). */
export interface CorrecoesPlanilha {
  versao: number;
  abas: Partial<Record<AbaPlanilhaExtracao, Record<string, CorrecaoCelulaPlanilha>>>;
}

export interface ExtracaoFaturaRow {
  id: string;
  created_at: string;
  origem: OrigemExtracao;
  lote_id: string | null;
  arquivo_nome: string | null;
  uc_codigo: string | null;
  mes_referencia: string | null;
  nome_cliente: string | null;
  cpf_cnpj: string | null;
  grupo: string | null;
  total_pagar: number | null;
  status: 'ok' | 'revisar' | 'falhou';
  score_auditoria: number | null;
  ocr_confidence: number | null;
  parser_version: string | null;
  pdf_storage_path: string | null;
  dados_estruturados: FaturaEstruturada | null;
  reconciliacao: ReconciliacaoResultado | null;
  usuario_nome: string | null;
  /** Correções manuais feitas na planilha (camada por cima de dados_estruturados). */
  correcoes_planilha?: CorrecoesPlanilha | null;
  corrigido_em?: string | null;
  corrigido_por_nome?: string | null;
}

export interface ExtracaoLoteRow {
  id: string;
  created_at: string;
  usuario_nome: string | null;
  qtd_total: number;
  qtd_ok: number;
  qtd_revisar: number;
  qtd_falhou: number;
}

/** Uma linha do relatório "Datas das faturas" (RPC `relatorio_datas_faturas`): uma fatura
 *  da distribuidora por UC + mês. Datas em ISO ('2026-06-30'). A data lida do PDF vence a
 *  gravada em `faturas`, que só cobre o que falta; nome e CPF vêm da fatura. */
export interface LinhaDatasFatura {
  fatura_id: number;
  /** Número da UC como a fatura foi gravada — ora o antigo, ora o novo. */
  uc: string;
  /** Unidade do cadastro (`unidades_consumidoras.id`); null quando a UC não está cadastrada.
   *  É a chave da linha: a mesma unidade com os dois números conta uma vez só. */
  uc_id?: number | null;
  /** Número novo da Equatorial (…012 + 2 dígitos), só dígitos. */
  uc_nova?: string | null;
  /** O outro número da unidade (o antigo), só dígitos. */
  uc_antiga?: string | null;
  cliente: string | null;
  cpf_cnpj: string | null;
  /** 'JUN/2026' */
  mes: string;
  /** '2026-06' */
  mes_iso: string;
  leitura_anterior: string | null;
  leitura_atual: string | null;
  proxima_leitura: string | null;
  apresentacao: string | null;
  vencimento: string | null;
  processamento: string | null;
  debito_automatico: boolean;
  /** Layout "Espelho da fatura" (sem boleto). */
  espelho: boolean;
  /** Nome da empresa parceira; null = fatura da Vigor. */
  empresa: string | null;
  tem_pdf: boolean;
  tem_extracao: boolean;
  /** O parser já procurou a data de processamento nesta extração. */
  processamento_lido: boolean;
  /** Boleto é guia de arrecadação (código começa com 8): não tem o campo DATA PROCESSAMENTO. */
  arrecadacao: boolean;
}

/** Uma linha do relatório "Datas dos boletos" (RPC `relatorio_datas_boletos`): o boleto que
 *  o VigorHub emitiu para a fatura (Vigor pelo Sicoob, parceira pelo Asaas). Uma linha por
 *  boleto. Datas em ISO ('2026-06-30'). */
export interface LinhaDatasBoleto {
  fatura_id: number;
  /** Número da UC como a fatura foi gravada — ora o antigo, ora o novo. */
  uc: string;
  /** Unidade do cadastro; mesma regra de `LinhaDatasFatura.uc_id`. */
  uc_id?: number | null;
  uc_nova?: string | null;
  uc_antiga?: string | null;
  cliente: string | null;
  cpf_cnpj: string | null;
  /** 'JUN/2026' */
  mes: string;
  /** '2026-06' */
  mes_iso: string;
  /** As gravadas na fatura (o que o boleto imprime); nos meses em que a fatura não tem, as
   *  lidas do PDF da Equatorial do mesmo mês. Avulso não tem leitura. */
  leitura_anterior: string | null;
  leitura_atual: string | null;
  proxima_leitura: string | null;
  /** Registro do boleto no banco (Sicoob/Asaas) — a "Emissão" impressa no boleto. */
  emissao: string | null;
  /** Dia em que o sistema mandou o boleto ao cliente (registrado a partir de MAR/2026). */
  envio: string | null;
  vencimento: string | null;
  pagamento: string | null;
  /** `status_boleto_vigor`: Pago, Em aberto, Vencido, Cancelado, Parcelado, Quitado. */
  status: string | null;
  num_cobranca: string | null;
  valor: number | null;
  /** Boleto avulso (acordo, parcela, multa), sem conta de energia do mês por trás. */
  avulso: boolean;
  /** Nome da empresa parceira; null = boleto da Vigor. */
  empresa: string | null;
}

/** Resposta da Edge Function `extrair-fatura`. */
export interface ExtracaoResposta {
  ok: boolean;
  error?: string;
  extracao_id?: string;
  estruturado?: FaturaEstruturada;
  reconciliacao?: ReconciliacaoResultado;
  reprocessado?: boolean;
  /** Extração deu certo, mas a gravação em extracoes_faturas falhou (best-effort:
   *  o trabalho de OCR não se perde). Os dados vêm mesmo assim, sem extracao_id. */
  persist_aviso?: string;
}

export interface EnderecoGoias {
  logradouro: string;
  numero: string;
  complemento: string;
  quadra: string;
  lote: string;
}

/** Resposta da Edge Function `extrair-fatura-crm` (uso exclusivo do CRM). */
export interface ExtracaoFaturaCrmResposta {
  ok: boolean;
  error?: string;
  /** Id da linha em extracoes_faturas (origem='crm'). null se a gravação
   *  best-effort falhou — a extração em si continua válida. */
  extracao_id?: string | null;
  estruturado?: FaturaEstruturada;
  endereco?: EnderecoGoias;
  reconciliacao?: ReconciliacaoResultado;
}

/** Resposta da Edge Function `extrair-documento-crm` (melhor esforço: RG/CNH/CNPJ/Contrato Social). */
export interface ExtracaoDocumentoCrmResposta {
  ok: boolean;
  error?: string;
  tipo_documento?: string;
  cliente_nome?: string;
  cpf_cnpj?: string;
}

/** Linha de retorno de get_usage_por_modulo — telemetria da aba Config > Uso. */
export interface UsoModuloRow {
  modulo: string;
  usuarios: number;
  eventos: number;
  usuarios_pct: number;
  tendencia_pct: number;
}

/** Linha de retorno de get_usage_por_funcionalidade. */
export interface UsoFuncionalidadeRow {
  funcionalidade: string;
  usuarios: number;
  eventos: number;
  usuarios_pct: number;
  tendencia_pct: number;
}

/** Agrupamento de faturamento (#5778): grupo de UCs de um cliente que fecha e cobra junto.
 *  Tabela `agrupamentos_faturamento`. Não se apaga — desativa (`ativo = false`). */
export interface AgrupamentoFaturamento {
  id: number;
  nome: string;
  /** Só dígitos (14). */
  cnpj_pagador: string;
  concessionaria: string;
  /**
   * DEPRECADO desde 16/09/2026: um agrupamento pode ter vários códigos (a SAP da Prefeitura tem
   * dois). Use `codigos`, carregado de `agrupamento_codigos_concessionaria`.
   */
  cod_agrupamento_concessionaria: string | null;
  /** Códigos na concessionária, texto com zeros à esquerda. Vem da tabela de códigos, não da linha. */
  codigos?: string[];
  /** 1..28, padrão 15. */
  dia_vencimento: number;
  ativo: boolean;
  pagador_nome: string | null;
  pagador_logradouro: string | null;
  pagador_cidade: string | null;
  pagador_uf: string | null;
  pagador_cep: string | null;
  /** Conta Sicoob que emite o boleto agrupado (#5785). NULL = ainda não definida. */
  conta_sicoob: string | null;
  /** % retido na fonte e descontado do boleto agrupado (#5785). 0 = boleto cheio. Opcional até a migration. */
  retencao_percentual?: number | string | null;
  /** Régua própria (#5787). NULL em cada campo = padrão do contrato. */
  dia_limite_emissao: number | null;
  dia_auditoria_util: number | null;
  dia_malote_util: number | null;
  dias_pagamento_edital: number | null;
  observacoes: string | null;
  criado_por: string | null;
  criado_em: string;
  atualizado_por: string | null;
  atualizado_em: string;
}

/** Campos editáveis pela tela; autoria e datas são carimbadas pelo banco. */
export type AgrupamentoFaturamentoInput = Pick<AgrupamentoFaturamento, 'nome' | 'cnpj_pagador'> &
  Partial<Pick<AgrupamentoFaturamento,
    'concessionaria' | 'cod_agrupamento_concessionaria' | 'dia_vencimento' | 'ativo' |
    'pagador_nome' | 'pagador_logradouro' | 'pagador_cidade' | 'pagador_uf' | 'pagador_cep' | 'observacoes' |
    'dia_limite_emissao' | 'dia_auditoria_util' | 'dia_malote_util' | 'dias_pagamento_edital'>>;

/** Linha da view `vw_agrupamento_faturamento_uc` (de-para UC ↔ agrupamento). UC sem agrupamento não aparece. */
export interface AgrupamentoFaturamentoUc {
  unidade_consumidora_id: number;
  numero_uc: string;
  nome_cliente: string | null;
  cpf_cnpj_cliente: string | null;
  agrupamento_id: number;
  agrupamento_nome: string;
  cnpj_pagador: string;
  concessionaria: string;
  /** Primeiro código em ordem (compatibilidade). Use `codigos_concessionaria`. */
  cod_agrupamento_concessionaria: string | null;
  dia_vencimento: number;
  agrupamento_ativo: boolean;
  vinculado_em: string;
  vinculado_por: string | null;
  /** Todos os códigos do agrupamento na concessionária. */
  codigos_concessionaria: string[];
}

/** Tabela `agrupamento_faturamento_ucs_log` — só inserção, pelas RPCs. */
export interface AgrupamentoFaturamentoUcLog {
  id: number;
  unidade_consumidora_id: number;
  numero_uc: string | null;
  acao: 'vinculada' | 'movida' | 'desvinculada';
  agrupamento_de_id: number | null;
  agrupamento_para_id: number | null;
  motivo: string | null;
  feito_por: string | null;
  feito_por_nome: string | null;
  feito_em: string;
}

/** Fechamento mensal de um agrupamento (#5783): tabela `agrupamento_fechamentos`.
 *  Nasce sozinho, com status 'aberto', na primeira vez que o mês é aberto na tela. */
export interface AgrupamentoFechamento {
  id: number;
  agrupamento_id: number;
  /** date, sempre no dia 1 do mês ("2026-08-01" = AGO/2026). */
  competencia: string;
  status: 'aberto' | 'fechado' | 'emitido' | 'cancelado';
  /** Foto do momento em que fechou. Zero é resultado válido. */
  total_vigor: number | null;
  ucs_no_grupo: number | null;
  ucs_resolvidas: number | null;
  observacoes: string | null;
  /** Boleto agrupado (#5785). Nosso número + linha digitável são a prova do registro. */
  num_cobranca_boleto: string | null;
  linha_digitavel_boleto: string | null;
  codigo_pix_boleto: string | null;
  url_boleto: string | null;
  emitido_em: string | null;
  emitido_por: string | null;
  fechado_em: string | null;
  fechado_por: string | null;
  /** Datas escritas à mão para este mês (#5787). NULL = vale a data calculada. */
  limite_emissao_ajustado: string | null;
  auditoria_ajustada: string | null;
  vencimento_ajustado: string | null;
  malote_ajustado: string | null;
  motivo_ajuste_datas: string | null;
  datas_ajustadas_por: string | null;
  datas_ajustadas_em: string | null;
  criado_por: string | null;
  criado_em: string;
  atualizado_por: string | null;
  atualizado_em: string;
}

/** Linha da tabela `feriados` (#5787): ajusta o cálculo padrão da régua de prazos.
 *  `tipo='inclui'` acrescenta um feriado; `tipo='exclui'` diz que a data NÃO é feriado. */
export interface FeriadoCadastro {
  id: number;
  data: string;
  nome: string;
  tipo: 'inclui' | 'exclui';
  abrangencia: 'nacional' | 'estadual' | 'municipal';
  uf: string | null;
  municipio: string | null;
  observacao: string | null;
  criado_por: string | null;
  criado_em: string;
  atualizado_por: string | null;
  atualizado_em: string;
}

/** Retorno normalizado de `vincular_ucs_agrupamento` / `desvincular_ucs_agrupamento`. */
export interface VincularUcsAgrupamentoResultado {
  ok: true;
  vinculadas: number;
  movidas: number;
  ja_estavam: number;
  desvinculadas: number;
  nao_estavam: number;
}

/** Linha de retorno de get_usage_por_acao. */
export interface UsoAcaoRow {
  acao: string;
  usuarios: number;
  eventos: number;
  usuarios_pct: number;
  tendencia_pct: number;
}
